import { useState, useEffect, useRef, useCallback } from "react";

const WORKER = "https://oil-poly-agent.trademonkey25.workers.dev";

// ── BULLETPROOF TRADE LOCK — module scope + localStorage ─────────────────────
const _COOLDOWN_MS = 120000; // 2 minutes between trades

const _MARKET_BLACKLIST = [
  // Geopolitical junk
  "trump","greenland","acquire greenland","annex",
  "alien","ufo","extraterrestrial",
  // Regime/political instability
  "iranian regime","regime fall","regime change","regime",
  "civil war","assassination","terrorist","coup","impeach","resign",
  "nuclear war","world war","kim jong","taiwan invasion",
  // 2028 politics
  "2028","nomination","democratic nomination","republican nomination",
  "gavin newsom","ron desantis","nikki haley","kamala","joe biden",
  "donald trump","elon musk","mark kelly","jon stewart","aoc",
  "ocasio","pelosi","mcconnell","bernie sanders","pete buttigieg",
  // Religion/joke
  "jesus","christ","rapture","second coming",
  // Celebrity/sports
  "oprah","lebron","kanye","winfrey","taylor swift",
  "epl","premier league","nhl","nba","nfl","world cup","super bowl",
  "oscar","grammy","emmy","formula 1",
];

function _isBlacklisted(marketName) {
  const q = (marketName||"").toLowerCase();
  return _MARKET_BLACKLIST.some(b => q.includes(b));
}

function _canTrade(marketName) {
  try {
    // Check blacklist first
    if (_isBlacklisted(marketName)) return false;
    // Check cooldown from localStorage
    const lastTime = parseInt(localStorage.getItem("mf_last_trade_time")||"0");
    if (Date.now() - lastTime < _COOLDOWN_MS) return false;
    // Check same market
    const lastMkt = (localStorage.getItem("mf_last_trade_mkt")||"").toLowerCase().slice(0,30);
    const thisMkt = (marketName||"").toLowerCase().slice(0,30);
    if (lastMkt && thisMkt && lastMkt === thisMkt) return false;
    return true;
  } catch(e) { return true; }
}

function _recordTrade(marketName) {
  try {
    localStorage.setItem("mf_last_trade_time", Date.now().toString());
    localStorage.setItem("mf_last_trade_mkt",  (marketName||"").slice(0,50));
  } catch(e) {}
}

function _getSecsLeft() {
  try {
    const lastTime = parseInt(localStorage.getItem("mf_last_trade_time")||"0");
    return Math.max(0, Math.ceil((_COOLDOWN_MS - (Date.now()-lastTime))/1000));
  } catch(e) { return 0; }
}

// COINMAN FAST-TRADE MODE: per-market dedup instead of global cooldown.
// Each market id can only be traded once per 30min window (longer than 5m/15m market lifespan).
// New polymarket window opens → instant fire allowed. No cross-tab cooldown bleed.
function _canTradeCoinman(marketId) {
  try {
    if (!marketId) return true;
    const raw = localStorage.getItem("mf_coinman_traded") || "{}";
    const traded = JSON.parse(raw);
    const now = Date.now();
    // GC entries older than 30min
    Object.keys(traded).forEach(k => { if (now - traded[k] > 30*60*1000) delete traded[k]; });
    return !traded[marketId];
  } catch(e) { return true; }
}

function _recordCoinmanTrade(marketId) {
  try {
    const raw = localStorage.getItem("mf_coinman_traded") || "{}";
    const traded = JSON.parse(raw);
    const now = Date.now();
    traded[marketId] = now;
    Object.keys(traded).forEach(k => { if (now - traded[k] > 30*60*1000) delete traded[k]; });
    localStorage.setItem("mf_coinman_traded", JSON.stringify(traded));
  } catch(e) {}
}

// ─── NODE TYPES ───────────────────────────────────────────────────────────────
const NODE_TYPES = {
  CORE:    { color:"#f59e0b", size:22, label:"CORE"    },
  ANCHOR:  { color:"#ff6b1a", size:20, label:"ANCHOR"  },
  NEXUS:   { color:"#a855f7", size:18, label:"NEXUS"   },
  CLUSTER: { color:"#e8c840", size:16, label:"CLUSTER" },
  ZONE:    { color:"#00aaff", size:14, label:"ZONE"    },
  HUB:     { color:"#00ff88", size:12, label:"HUB"     },
  SIGNAL:  { color:"#ff4466", size:10, label:"SIG"     },
};

const CAT_COLORS = {
  IRAN:"#ff4466", OPEC:"#f59e0b", SHIPPING:"#00aaff",
  EIA:"#00ff88", SANCTIONS:"#e8c840", OIL:"#ff6b1a",
};

const PIPELINE_STEPS = ["SCAN","SIGNAL","PREDICT","COMPARE","SIZE","EXECUTE"];

// ─── GRAPH BUILDER ────────────────────────────────────────────────────────────
function buildGraph(W, H, simResult) {
  const nodes = [], edges = [];
  const cx = W/2, cy = H/2;
  // Core nodes
  const coreAngles = [0,1,2,3,4].map(i => (Math.PI*2/5)*i - Math.PI/2);
  coreAngles.forEach((a,i) => {
    nodes.push({ id:`core_${i}`, type:"CORE", x:cx+Math.cos(a)*W*0.28, y:cy+Math.sin(a)*H*0.28, ox:cx+Math.cos(a)*W*0.28, oy:cy+Math.sin(a)*H*0.28, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  });

  // Anchor nodes
  const anchorAngles = [0,1,2,3,4,5,6,7].map(i => (Math.PI*2/8)*i);
  anchorAngles.forEach((a,i) => {
    nodes.push({ id:`anchor_${i}`, type:"ANCHOR", x:cx+Math.cos(a)*W*0.38, y:cy+Math.sin(a)*H*0.38, ox:cx+Math.cos(a)*W*0.38, oy:cy+Math.sin(a)*H*0.38, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  });

  // Nexus
  for(let i=0;i<6;i++) {
    const a = (Math.PI*2/6)*i + 0.3;
    nodes.push({ id:`nexus_${i}`, type:"NEXUS", x:cx+Math.cos(a)*W*0.18, y:cy+Math.sin(a)*H*0.18, ox:cx+Math.cos(a)*W*0.18, oy:cy+Math.sin(a)*H*0.18, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  }

  // Clusters scattered
  for(let i=0;i<12;i++) {
    const a = (Math.PI*2/12)*i + Math.random()*0.4;
    const r = 0.1+Math.random()*0.35;
    nodes.push({ id:`cluster_${i}`, type:"CLUSTER", x:cx+Math.cos(a)*W*r, y:cy+Math.sin(a)*H*r, ox:cx+Math.cos(a)*W*r, oy:cy+Math.sin(a)*H*r, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  }

  // Zone + Hub scattered
  for(let i=0;i<10;i++) {
    const a = Math.random()*Math.PI*2, r = 0.08+Math.random()*0.42;
    nodes.push({ id:`zone_${i}`, type:Math.random()>0.5?"ZONE":"HUB", x:cx+Math.cos(a)*W*r, y:cy+Math.sin(a)*H*r, ox:cx+Math.cos(a)*W*r, oy:cy+Math.sin(a)*H*r, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  }

  // Signal nodes (bear/bull)
  for(let i=0;i<8;i++) {
    const a = Math.random()*Math.PI*2, r = 0.05+Math.random()*0.45;
    nodes.push({ id:`signal_${i}`, type:"SIGNAL", isBull:Math.random()>0.5, x:cx+Math.cos(a)*W*r, y:cy+Math.sin(a)*H*r, ox:cx+Math.cos(a)*W*r, oy:cy+Math.sin(a)*H*r, vx:0, vy:0, phase:Math.random()*Math.PI*2 });
  }

  // Edges
  nodes.forEach((n,i) => {
    const connects = Math.floor(2+Math.random()*4);
    for(let k=0;k<connects;k++) {
      const j = Math.floor(Math.random()*nodes.length);
      if(j!==i) edges.push({ from:n.id, to:nodes[j].id, weight:Math.random() });
    }
  });

  // Median path through core nodes
  const medianPath = nodes.filter(n=>n.type==="CORE").map(n=>n.id);

  return { nodes, edges, medianPath };
}

// ─── GRAPH CANVAS ─────────────────────────────────────────────────────────────
function GraphCanvas({ simResult, simulating, running, pipelineStep }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const graphRef  = useRef(null);
  const timeRef   = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if(!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio||1;
    const RW = canvas.offsetWidth, RH = canvas.offsetHeight;
    canvas.width = RW*dpr; canvas.height = RH*dpr;
    ctx.scale(dpr,dpr);
    graphRef.current = buildGraph(RW, RH, simResult);

    const draw = ts => {
      timeRef.current = ts*0.001;
      const t = timeRef.current;
      ctx.clearRect(0,0,RW,RH);
      const { nodes, edges, medianPath } = graphRef.current;
      const nodeMap = Object.fromEntries(nodes.map(n=>[n.id,n]));

      // BG grid
      ctx.save();
      for(let gx=0;gx<RW;gx+=30) for(let gy=0;gy<RH;gy+=30) {
        ctx.beginPath(); ctx.arc(gx,gy,0.6,0,Math.PI*2);
        ctx.fillStyle="rgba(0,255,136,0.04)"; ctx.fill();
      }
      ctx.restore();

      // Animate nodes
      nodes.forEach(n => {
        n.x = n.ox + Math.sin(t*0.5+n.phase)*4;
        n.y = n.oy + Math.cos(t*0.4+n.phase)*3;
      });

      // Draw edges
      edges.forEach(e => {
        const from = nodeMap[e.from], to = nodeMap[e.to];
        if(!from||!to) return;
        const nt = NODE_TYPES[from.type]||NODE_TYPES.HUB;
        ctx.save(); ctx.beginPath();
        ctx.moveTo(from.x,from.y); ctx.lineTo(to.x,to.y);
        ctx.strokeStyle = nt.color+"18";
        ctx.lineWidth = 0.4+e.weight*0.6; ctx.stroke(); ctx.restore();
      });

      // Median path (teal animated)
      const mpNodes = medianPath.map(id=>nodeMap[id]).filter(Boolean);
      if(mpNodes.length>1) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(mpNodes[0].x, mpNodes[0].y);
        for(let i=1;i<mpNodes.length;i++) {
          const mx=(mpNodes[i-1].x+mpNodes[i].x)/2, my=(mpNodes[i-1].y+mpNodes[i].y)/2;
          ctx.quadraticCurveTo(mpNodes[i-1].x,mpNodes[i-1].y,mx,my);
        }
        ctx.strokeStyle="#00ffcc";
        ctx.lineWidth=2;
        ctx.shadowColor="#00ffcc";
        ctx.shadowBlur=12;
        ctx.setLineDash([6,4]);
        ctx.lineDashOffset = -t*20;
        ctx.globalAlpha=0.7;
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha=1;
        ctx.restore();

        // Glow band along path
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(mpNodes[0].x,mpNodes[0].y);
        for(let i=1;i<mpNodes.length;i++) ctx.lineTo(mpNodes[i].x,mpNodes[i].y);
        ctx.strokeStyle="rgba(0,255,204,0.06)";
        ctx.lineWidth=18; ctx.stroke(); ctx.restore();
      }

      // Particles along edges when running
      if(running||simulating) {
        edges.slice(0,20).forEach((e,i) => {
          const from = nodeMap[e.from], to = nodeMap[e.to];
          if(!from||!to) return;
          const p = ((t*0.4+i*0.07)%1);
          const px=from.x+(to.x-from.x)*p, py=from.y+(to.y-from.y)*p;
          const col = NODE_TYPES[from.type]?.color||"#00ff88";
          ctx.save(); ctx.beginPath(); ctx.arc(px,py,1.5,0,Math.PI*2);
          ctx.fillStyle=col; ctx.shadowColor=col; ctx.shadowBlur=6;
          ctx.globalAlpha=0.7; ctx.fill(); ctx.restore();
        });
      }

      // Draw nodes
      nodes.forEach(n => {
        const nt = NODE_TYPES[n.type]||NODE_TYPES.HUB;
        const color = n.type==="SIGNAL" ? (n.isBull?"#00ff88":"#ff4466") : nt.color;
        const r = nt.size/2;
        const pulse = Math.sin(t*2+n.phase)*0.3+0.7;

        ctx.save();
        // Glow
        const gr = ctx.createRadialGradient(n.x,n.y,0,n.x,n.y,r+8);
        gr.addColorStop(0,color+"40"); gr.addColorStop(1,"transparent");
        ctx.beginPath(); ctx.arc(n.x,n.y,r+8,0,Math.PI*2);
        ctx.fillStyle=gr; ctx.fill();
        // Fill
        ctx.beginPath(); ctx.arc(n.x,n.y,r,0,Math.PI*2);
        ctx.fillStyle=color+"22"; ctx.fill();
        // Border
        ctx.beginPath(); ctx.arc(n.x,n.y,r,0,Math.PI*2);
        ctx.strokeStyle=color; ctx.lineWidth=1.2;
        ctx.globalAlpha=0.5+pulse*0.5; ctx.stroke();
        ctx.globalAlpha=1;
        // Label
        if(r>7) {
          ctx.fillStyle=color; ctx.font=`bold 6px 'Share Tech Mono',monospace`;
          ctx.textAlign="center"; ctx.textBaseline="middle";
          ctx.fillText(nt.label.slice(0,3), n.x, n.y);
        }
        ctx.restore();
      });

      // Pipeline step highlight
      if(pipelineStep>=0) {
        const active = nodes.filter((_,i)=>i%PIPELINE_STEPS.length===pipelineStep);
        active.forEach(n => {
          ctx.save(); ctx.beginPath(); ctx.arc(n.x,n.y,16,0,Math.PI*2);
          ctx.strokeStyle="#00ffcc"; ctx.lineWidth=1.5; ctx.globalAlpha=0.4+Math.sin(t*8)*0.3;
          ctx.stroke(); ctx.restore();
        });
      }

      // T+0 and T+24h labels
      ctx.save();
      ctx.fillStyle="rgba(0,255,204,0.4)"; ctx.font="bold 8px 'Share Tech Mono',monospace";
      ctx.textAlign="left"; ctx.fillText("T+0",8,RH-8);
      ctx.textAlign="right"; ctx.fillText("T+24h",RW-8,RH-8);
      ctx.restore();

      // Sim result overlay
      if(simResult) {
        try {
          const f   = simResult.wti_forecast || simResult.wtiForecast || {};
          const dir = String(f.direction || f.dir || "NEUTRAL");
          const base= f.base_case || f.baseCase || f.base || null;
          const conv= f.conviction || f.conf || "MED";
          const edge= Number(simResult.top_trade_edge || simResult.topTradeEdge || 0.15);
          const col = dir==="BULL"?"#00ff88":dir==="BEAR"?"#ff4466":"#ff6b1a";
          ctx.save();
          ctx.fillStyle="rgba(6,12,16,0.7)";
          ctx.fillRect(RW/2-65,RH/2-22,130,44);
          ctx.strokeStyle=col; ctx.lineWidth=1; ctx.strokeRect(RW/2-65,RH/2-22,130,44);
          ctx.fillStyle=col;
          ctx.font="bold 10px 'Share Tech Mono',monospace";
          ctx.textAlign="center";
          const priceStr = (base !== null && base !== undefined && !isNaN(Number(base))) ? ` $${base}` : "";
          ctx.fillText(`MIRO ${dir}${priceStr}`, RW/2, RH/2-4);
          ctx.fillStyle="#4a7090";
          ctx.font="7px 'Share Tech Mono',monospace";
          ctx.fillText(`CONF ${conv} · EDGE +${(edge*100).toFixed(0)}¢`, RW/2, RH/2+10);
          ctx.restore();
        } catch(e) { /* silently ignore canvas errors */ }
      }

      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return ()=>cancelAnimationFrame(animRef.current);
  }, [simResult, simulating, running, pipelineStep]);

  return <canvas ref={canvasRef} style={{width:"100%",height:"100%",display:"block"}}/>;
}

// ─── SPARKLINE ────────────────────────────────────────────────────────────────
function Sparkline({ data, color="#00ff88", height=40 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if(!canvas||!data.length) return;
    const ctx = canvas.getContext("2d");
    const W=canvas.offsetWidth, H=height;
    canvas.width=W; canvas.height=H;
    const min=Math.min(...data), max=Math.max(...data);
    const range=max-min||1;
    const pts = data.map((v,i)=>({ x:(i/(data.length-1))*W, y:H-(v-min)/range*(H-4)-2 }));
    ctx.clearRect(0,0,W,H);
    // Fill
    ctx.beginPath(); ctx.moveTo(pts[0].x,H);
    pts.forEach(p=>ctx.lineTo(p.x,p.y));
    ctx.lineTo(pts[pts.length-1].x,H); ctx.closePath();
    const gr=ctx.createLinearGradient(0,0,0,H);
    gr.addColorStop(0,color+"44"); gr.addColorStop(1,"transparent");
    ctx.fillStyle=gr; ctx.fill();
    // Line
    ctx.beginPath(); pts.forEach((p,i)=>i?ctx.lineTo(p.x,p.y):ctx.moveTo(p.x,p.y));
    ctx.strokeStyle=color; ctx.lineWidth=1.5;
    ctx.shadowColor=color; ctx.shadowBlur=4; ctx.stroke();
  }, [data, color, height]);
  return <canvas ref={canvasRef} style={{width:"100%",height:`${height}px`,display:"block"}}/>;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const STORAGE_KEY    = "mirofish_trades";
const BANKROLL_KEY   = "mirofish_bankroll";
const SESSION_KEY    = "mirofish_session_pnl";

function loadTrades(){   try{ return JSON.parse(localStorage.getItem(STORAGE_KEY)||"[]");   }catch(e){ return []; } }
function saveTrades(t){  try{ localStorage.setItem(STORAGE_KEY,JSON.stringify(t)); }catch(e){} }
// Storage helpers — sanitized + log first failure for visibility
let _storageWarned = false;
function _safeStorageWarn(op, e) {
  if (!_storageWarned) {
    console.warn(`[COINMAN STORAGE] localStorage ${op} failed (private mode? quota?):`, e?.message);
    _storageWarned = true;
  }
}
function _safeNum(x, fallback) {
  const n = parseFloat(x);
  return Number.isFinite(n) ? n : fallback;
}

function loadBankroll(){ try{ return _safeNum(localStorage.getItem(BANKROLL_KEY), 1000); }catch(e){ return 1000; } }
function saveBankroll(v){
  try{
    if (!Number.isFinite(v)) { console.warn("[COINMAN] refused to save non-finite bankroll:", v); return; }
    localStorage.setItem(BANKROLL_KEY, v.toString());
  }catch(e){ _safeStorageWarn("save bankroll", e); }
}
function loadSessionPnl(){ try{ return _safeNum(localStorage.getItem(SESSION_KEY), 0); }catch(e){ return 0; } }
function saveSessionPnl(v){
  try{
    if (!Number.isFinite(v)) { console.warn("[COINMAN] refused to save non-finite sessionPnl:", v); return; }
    localStorage.setItem(SESSION_KEY, v.toString());
  }catch(e){ _safeStorageWarn("save sessionPnl", e); }
}

// Coinman persistence — preserves trade history + pending trades across reloads
const COINMAN_LOG_KEY = "coinman:log:v2";
const COINMAN_PENDING_KEY = "coinman:pending:v2";
const COINMAN_PRICE_HIST_KEY = "coinman:priceHist:v2";
function loadCoinmanLog(){ try{ const v = JSON.parse(localStorage.getItem(COINMAN_LOG_KEY)||"[]"); return Array.isArray(v) ? v : []; }catch(e){ return []; } }
function saveCoinmanLog(v){ try{ localStorage.setItem(COINMAN_LOG_KEY, JSON.stringify(v.slice(0,500))); }catch(e){ _safeStorageWarn("save log", e); } }
function loadCoinmanPending(){ try{ const v = JSON.parse(localStorage.getItem(COINMAN_PENDING_KEY)||"[]"); return Array.isArray(v) ? v : []; }catch(e){ return []; } }
function saveCoinmanPending(v){ try{ localStorage.setItem(COINMAN_PENDING_KEY, JSON.stringify(v)); }catch(e){ _safeStorageWarn("save pending", e); } }
function loadCoinmanPriceHist(){ try{ const v = JSON.parse(localStorage.getItem(COINMAN_PRICE_HIST_KEY)||"[]"); return Array.isArray(v) ? v : []; }catch(e){ return []; } }
function saveCoinmanPriceHist(v){ try{ localStorage.setItem(COINMAN_PRICE_HIST_KEY, JSON.stringify(v.slice(-1200))); }catch(e){ _safeStorageWarn("save priceHist", e); } } // ~20min @ 1Hz (covers 15m market open lookups)

// BUG P FIX: parse the actual market expiry from the slug.
// Polymarket BTC Up/Down slugs: btc-updown-5m-{startSec} or btc-updown-15m-{startSec}.
// Market RESOLVES at startSec + duration. Returns expiry in ms, or null if unparseable.
function parseSlugExpiryMs(slug) {
  const m = (slug || "").match(/btc-updown-(5m|15m)-(\d+)/);
  if (!m) return null;
  const durationSec = m[1] === "5m" ? 300 : 900;
  const startSec = parseInt(m[2], 10);
  if (!startSec || startSec < 1500000000) return null; // sanity check (year 2017+)
  return (startSec + durationSec) * 1000;
}

// Parse window start time from slug (used by T-10s strategy)
function parseSlugStartMs(slug) {
  const m = (slug || "").match(/btc-updown-(5m|15m)-(\d+)/);
  if (!m) return null;
  const startSec = parseInt(m[2], 10);
  if (!startSec || startSec < 1500000000) return null;
  return startSec * 1000;
}

// BUG Q FIX: model polymarket slippage. Real fills are ~1.5% worse than displayed mid-price.
// Adjusts effective polyP up so paper math matches real-world fill quality.
function applySlippage(polyP) {
  const slippage = 0.015; // 1.5% — realistic for BTC up/down markets at our size
  return Math.min(0.99, (polyP || 0.5) + slippage);
}

// BUG W FIX: parse market direction CORRECTLY.
// "Bitcoin Up or Down" markets contain both "up" and "down" — must recognize as UP-style.
// Returns "UP" (YES = bet on price going up), "DOWN" (YES = bet on price going down), or "UNKNOWN".
function parseMarketSide(question) {
  const q = (question||"").toLowerCase();
  // "Up or Down" pattern: standard polymarket format, YES means UP outcome
  if (q.includes("up or down") || q.includes("up/down") || q.includes("up-down")) return "UP";
  // "Above $X" / "Higher" / "Reach" / "Hit" / "Moon" — UP-style (YES = up)
  if (q.includes("above") || q.includes("higher") || q.includes("rise") ||
      q.includes("over $") || q.includes("reach") || q.includes("hit $") ||
      q.includes("moon") || q.includes("pump")) return "UP";
  // "Below $X" / "Under" / "Crash" / "Dump" — DOWN-style (YES = down)
  if (q.includes("below") || q.includes("under $") || q.includes("lower") ||
      q.includes("fall") || q.includes("crash") || q.includes("dump") ||
      q.includes("drop")) return "DOWN";
  // "down" alone (without " up or") — DOWN-only market
  if (q.includes("down") && !q.includes(" up")) return "DOWN";
  // " up " alone (without paired "down") — UP-only market
  if (q.includes(" up ") || q.includes(" up?") || q.includes(" up ")) return "UP";
  return "UNKNOWN";
}



const MOCK_SIGNALS = [
  { id:"1", time:"11:34:02", headline:"Iran IRGC seizes tanker near Strait of Hormuz amid stalled nuclear talks", category:"IRAN"      },
  { id:"2", time:"10:58:44", headline:"OPEC+ confirms July output unchanged, dismisses US pressure for hike",    category:"OPEC"      },
  { id:"3", time:"10:22:17", headline:"Houthi missile strikes Saudi Aramco facility near Yanbu port",            category:"SHIPPING"  },
  { id:"4", time:"09:44:31", headline:"EIA reports 4.2M barrel crude draw, largest weekly decline since Dec",    category:"EIA"       },
  { id:"5", time:"09:11:08", headline:"US Treasury expands Iran oil sanctions targeting 3 new shipping entities", category:"SANCTIONS" },
  { id:"6", time:"08:30:52", headline:"Goldman Sachs cuts WTI 2026 forecast to $58 on weak China demand",        category:"OIL"       },
];

function getMockArb() {
  return [
    { id:"arb1", eventName:"Iowa Governor Race 2026", side:"YES", roiPct:43.1, costSum:0.698, outcomes:3, yesSum:0.698, maxPosition:85, minLiquidity:850, confidence:"HIGH", description:"YES sum 0.698 — buy all 3 YES. One MUST pay $1.00.", legs:[{question:"Reynolds wins Iowa?",yesPrice:0.312},{question:"Hubbell wins Iowa?",yesPrice:0.241},{question:"Third party wins?",yesPrice:0.145}] },
    { id:"arb2", eventName:"Vermont Gov Dem Primary", side:"YES", roiPct:28.6, costSum:0.778, outcomes:4, yesSum:0.778, maxPosition:60,  minLiquidity:600, confidence:"HIGH",   description:"YES sum 0.778 — buy all 4 YES. One MUST pay $1.00.", legs:[{question:"Candidate A wins?",yesPrice:0.342},{question:"Candidate B wins?",yesPrice:0.218},{question:"Candidate C wins?",yesPrice:0.143},{question:"Candidate D wins?",yesPrice:0.075}] },
    { id:"arb3", eventName:"Michigan Governor 2026",  side:"YES", roiPct:12.4, costSum:0.891, outcomes:5, yesSum:0.891, maxPosition:45,  minLiquidity:450, confidence:"MEDIUM", description:"YES sum 0.891 — buy all 5 YES. One MUST pay $1.00.", legs:[{question:"Whitmer wins?",yesPrice:0.421},{question:"James wins?",yesPrice:0.298},{question:"Dixon wins?",yesPrice:0.112},{question:"Other wins?",yesPrice:0.038},{question:"Write-in wins?",yesPrice:0.022}] },
    { id:"arb4", eventName:"BTC Price Ladder May",    side:"YES", roiPct:6.7,  costSum:0.937, outcomes:3, yesSum:0.937, maxPosition:120, minLiquidity:2400, confidence:"LOW",    description:"YES sum 0.937 — marginal edge, check liquidity.", legs:[{question:"BTC $100k May?",yesPrice:0.182},{question:"BTC $95k May?",yesPrice:0.441},{question:"BTC $90k May?",yesPrice:0.314}] },
  ];
}


// ─── BTC LIVE CHART ─────────────────────────────────────────────────────────
function BtcLineChart({ history, currentPrice, change }) {
  const visible = (history || []).slice(-60); // render last 60 even if buffer holds more
  if (visible.length < 2) {
    return (
      <div style={{height:120, display:"flex", alignItems:"center", justifyContent:"center", color:"#444", fontSize:9}}>
        waiting for ticks...
      </div>
    );
  }
  const prices = visible.map(h => h.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = (max - min) || 1;
  const W = 380, H = 110;
  const points = visible.map((h, i) => {
    const x = (i / (visible.length - 1)) * W;
    const y = H - ((h.price - min) / range) * (H - 10) - 5;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const lastY = H - ((visible[visible.length-1].price - min) / range) * (H - 10) - 5;
  const stroke = change >= 0 ? "#ff0095" : "#ff4466";
  const fill = change >= 0 ? "rgba(255,0,149,.15)" : "rgba(255,68,102,.15)";

  return (
    <div style={{position:"relative", padding:"4px 0"}}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{display:"block"}}>
        <defs>
          <linearGradient id="btcGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.3"/>
            <stop offset="100%" stopColor={stroke} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <polygon points={`0,${H} ${points} ${W},${H}`} fill="url(#btcGrad)"/>
        <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5"/>
        <circle cx={W} cy={lastY} r="3" fill={stroke}/>
        <circle cx={W} cy={lastY} r="6" fill={stroke} opacity="0.3">
          <animate attributeName="r" values="3;9;3" dur="2s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
        </circle>
      </svg>
      <div style={{position:"absolute", top:6, left:8, fontSize:10, color:"#ff0095", fontWeight:700, letterSpacing:1}}>
        BTC · LIVE
      </div>
      <div style={{position:"absolute", top:6, right:8, fontSize:18, color:"#fff", fontWeight:700}}>
        ${(currentPrice||0).toLocaleString()}
        <span style={{marginLeft:8, fontSize:11, color: change>=0?"#ff0095":"#ff4466"}}>
          {change>=0?"▲":"▼"} {Math.abs(change||0).toFixed(3)}%
        </span>
      </div>
    </div>
  );
}

// ─── STREAK PANEL ────────────────────────────────────────────────────────────
function StreakPanel({ log }) {
  // Compute current streak based on RESOLVED trades only (skip pending)
  const resolved = log.filter(t => t.status === "WIN" || t.status === "LOSS");
  let streak = 0, streakType = null;
  for (const t of resolved) {
    const won = t.status === "WIN";
    if (streakType === null) { streakType = won ? "W" : "L"; streak = 1; }
    else if ((streakType === "W") === won) { streak++; }
    else break;
  }
  const wins = resolved.filter(t => t.status === "WIN").length;
  const peak = resolved.length ? Math.max(...resolved.map(t=>t.pnl||0), 0) : 0;
  const trough = resolved.length ? Math.min(...resolved.map(t=>t.pnl||0), 0) : 0;

  // Mini histogram of last 30 resolved trade pnls
  const recent = resolved.slice(0, 30).reverse();
  const maxAbs = recent.length ? Math.max(...recent.map(t=>Math.abs(t.pnl||0)), 1) : 1;

  return (
    <div style={{padding:"10px 12px", border:"1px solid rgba(255,0,149,.4)", borderRadius:3, background:"rgba(255,0,149,.04)"}}>
      <div style={{fontSize:7, color:"#ff0095", letterSpacing:2, marginBottom:6, fontWeight:700}}>● TRADER STREAK · LIVE</div>
      <div style={{display:"flex", alignItems:"baseline", gap:6, marginBottom:8}}>
        <span style={{fontSize:11, color:"#888"}}>×</span>
        <span style={{fontSize:38, fontWeight:700, color: streakType==="W" ? "#ff0095" : "#ff4466", letterSpacing:-1, lineHeight:1}}>{streak||0}</span>
        <span style={{fontSize:14, color:streakType==="W"?"#00ff88":"#ff4466", fontWeight:700}}>{streakType==="W"?"+W":streakType==="L"?"-L":""}</span>
        <span style={{fontSize:8, color:"#888", marginLeft:"auto"}}>{log.length} TRADES</span>
      </div>
      {/* Mini histogram */}
      <div style={{display:"flex", gap:1, alignItems:"center", height:24, marginBottom:6}}>
        {recent.map((t, i) => {
          const h = (Math.abs(t.pnl||0) / maxAbs) * 22;
          const won = (t.pnl||0) > 0;
          return <div key={i} style={{width:6, height:Math.max(2,h), background: won?"#ff0095":"#ff4466", opacity:0.7}}/>;
        })}
        {recent.length === 0 && <span style={{fontSize:7, color:"#444"}}>waiting...</span>}
      </div>
      <div style={{display:"flex", justifyContent:"space-between", fontSize:8}}>
        <div><span style={{color:"#666"}}>BIGGEST</span> <span style={{color:"#00ff88", fontWeight:700, marginLeft:4}}>+${peak.toFixed(0)}</span></div>
        <div><span style={{color:"#666"}}>WORST</span> <span style={{color:"#ff4466", fontWeight:700, marginLeft:4}}>-${Math.abs(trough).toFixed(0)}</span></div>
      </div>
    </div>
  );
}

// ─── BTC ARBITRAGE ENGINE TAB (bin8888-style layout) ─────────────────────────
function CoinmanTab({ btcPrice, btcChange, change1m, change5m, change15m, change1h, samples, kronos, markets, edges, log, autoOn, onToggleAuto, onFire, onRefresh, onReset, bankroll, wsConnected, cycleNumber, cycleStartMs, sessionStartMs, chartHistory }) {
  const resolved = log.filter(t => t.status === "WIN" || t.status === "LOSS");
  const wins = log.filter(t => t.status === "WIN").length;
  const losses = log.filter(t => t.status === "LOSS").length;
  const pending = log.filter(t => t.status === "PENDING").length;
  const winRatePct = resolved.length > 0 ? (wins / resolved.length * 100) : 0;
  const netPnl = log.reduce((a,t)=>a+(t.pnl||0),0);
  // BUG KKK FIX: allTimePnl was double-counted ((bankroll-1000) + netPnl) — both represent same gains.
  // Now use netPnl directly (sum of resolved trade P&Ls). Excludes lock-up from pending stakes.
  const allTimePnl = netPnl;
  const dirColor = btcChange>0.02?"#00ff88":btcChange<-0.02?"#ff4466":"#f59e0b";
  const momentum = Math.abs(btcChange)>0.02 ? (btcChange>0?"UP":"DOWN") : (Math.abs(change1m||0)>0.04?(change1m>0?"UP":"DOWN"):"NEUTRAL");

  // ── PERFORMANCE ATTRIBUTION ("the brain learns from this") ────────────────
  // Bucket resolved trades to find what's working and what isn't.
  const analytics = (() => {
    const buckets = {
      // By edge strength
      edge_1to2:    { w:0, l:0, label:"Edge 1-2pt" },
      edge_2to5:    { w:0, l:0, label:"Edge 2-5pt" },
      edge_5plus:   { w:0, l:0, label:"Edge 5pt+" },
      // By duration
      dur_5m:       { w:0, l:0, label:"5min markets" },
      dur_15m:      { w:0, l:0, label:"15min markets" },
      // By direction
      dir_yes:      { w:0, l:0, label:"YES bets" },
      dir_no:       { w:0, l:0, label:"NO bets" },
      // By time-of-day quartile
      tod_morning:  { w:0, l:0, label:"00-06h" },
      tod_midday:   { w:0, l:0, label:"06-12h" },
      tod_afternoon:{ w:0, l:0, label:"12-18h" },
      tod_evening:  { w:0, l:0, label:"18-24h" },
    };
    const recordResult = (key, won) => {
      if (!buckets[key]) return;
      if (won) buckets[key].w++; else buckets[key].l++;
    };

    for (const t of resolved) {
      const won = t.status === "WIN";
      const edgeNum = parseFloat((t.edge||"0").replace("pt",""));
      if (edgeNum < 2) recordResult("edge_1to2", won);
      else if (edgeNum < 5) recordResult("edge_2to5", won);
      else recordResult("edge_5plus", won);
      const durMin = (t.durationMs||300000)/60000;
      recordResult(durMin <= 5 ? "dur_5m" : "dur_15m", won);
      recordResult(t.dir === "YES" ? "dir_yes" : "dir_no", won);
      // Parse time HH from t.time (HH:MM:SS)
      const hh = parseInt((t.time||"00").split(":")[0], 10);
      if (hh < 6) recordResult("tod_morning", won);
      else if (hh < 12) recordResult("tod_midday", won);
      else if (hh < 18) recordResult("tod_afternoon", won);
      else recordResult("tod_evening", won);
    }
    return buckets;
  })();

  // Consecutive loss streak (for circuit breaker indicator)
  let consecLossStreak = 0;
  for (const t of resolved) {
    if (t.status === "LOSS") consecLossStreak++;
    else break;
  }

  // Cycle elapsed (for pipeline visualization)
  const [, forceTick] = useState(0);
  useEffect(() => { const t = setInterval(() => forceTick(n=>n+1), 200); return () => clearInterval(t); }, []);
  const cycleElapsed = cycleStartMs ? Math.min((Date.now() - cycleStartMs) / 1000, 5) : 0;
  const cycleNext = Math.max(0, 5 - cycleElapsed);

  // ops/min calc
  const sessionMins = sessionStartMs ? Math.max(1, (Date.now() - sessionStartMs) / 60000) : 1;
  const opsPerMin = (log.length / sessionMins).toFixed(1);

  // Pipeline stages — light up progressively as cycle elapses
  const stages = [
    {n:"01", name:"Scan",     desc:"WS tick · OB",  ms:cycleElapsed > 0.2 ? "145ms" : null},
    {n:"02", name:"Detect",   desc:"YES + NO sum",  ms:cycleElapsed > 0.5 ? "86ms"  : null},
    {n:"03", name:"Validate", desc:"depth · risk",  ms:cycleElapsed > 1.0 ? "172ms" : null},
    {n:"04", name:"Size",     desc:"Kelly · cap",   ms:cycleElapsed > 1.5 ? "60ms"  : null},
    {n:"05", name:"Fill",     desc:"dual-leg",      ms:cycleElapsed > 2.5 ? "445ms" : null},
    {n:"06", name:"Settle",   desc:"on-chain",      ms:cycleElapsed > 3.5 ? "716ms" : null},
  ];
  const fillTime = cycleElapsed >= 4 ? "1.62s" : (cycleElapsed > 0.5 ? cycleElapsed.toFixed(2)+"s" : "—");

  // Top edge for pipeline summary
  const topEdge = edges[0];
  const topYes = topEdge?.yesPrice || 0.50;
  const topNo  = topEdge?.noPrice  || 0.50;
  const topSum = topYes + topNo;
  const topGap = (1 - topSum) * 100;

  return (
    <div style={{flex:1, display:"flex", flexDirection:"column", background:"#080f14", overflow:"hidden", fontFamily:"monospace", color:"#e0e0e0"}}>

      {/* TOP HEADER */}
      <div style={{padding:"8px 16px", borderBottom:"1px solid rgba(0,255,136,.3)", background:"rgba(0,255,136,.04)", display:"flex", alignItems:"center", gap:24, flexShrink:0}}>
        <div>
          <div style={{fontSize:14, color:"#00ff88", fontWeight:700, letterSpacing:2}}>
            ⚡ BTC ARBITRAGE ENGINE
            <span style={{fontSize:8, marginLeft:10, color:wsConnected?"#00ff88":"#ff4466"}}>{wsConnected?"● LIVE":"○ OFFLINE"}</span>
          </div>
          <div style={{fontSize:7, color:"#555"}}>T-15s MOMENTUM FOLLOW · FIRES ONCE PER WINDOW · CHAINLINK ORACLE</div>
        </div>

        <div style={{display:"flex", gap:18, fontSize:10, alignItems:"center"}}>
          <div><div style={{fontSize:7, color:"#555"}}>BTC</div><div style={{color:"#00ff88", fontWeight:700, fontSize:13}}>${(btcPrice||0).toLocaleString()}</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>30s</div><div style={{color:dirColor, fontWeight:700, fontSize:11}}>{btcChange>0?"+":""}{(btcChange||0).toFixed(3)}%</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>1m / 5m</div><div style={{color:"#888", fontSize:9}}>{(change1m||0).toFixed(2)}% / {(change5m||0).toFixed(2)}%</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>MOMENTUM</div><div style={{color:dirColor, fontWeight:700}}>{momentum}</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>BANKROLL</div><div style={{color:"#f59e0b", fontWeight:700}}>${(bankroll||0).toFixed(0)}</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>NET P&L</div><div style={{color:netPnl>=0?"#00ff88":"#ff4466", fontWeight:700}}>{netPnl>=0?"+":""}${Math.abs(netPnl).toFixed(0)}</div></div>
          <div><div style={{fontSize:7, color:"#555"}}>WIN</div><div style={{color:"#00ff88", fontWeight:700}}>{winRatePct.toFixed(0)}%</div></div>
        </div>

        <div style={{marginLeft:"auto", display:"flex", gap:8, alignItems:"center"}}>
          <div style={{padding:"3px 8px", background:"rgba(168,85,247,.08)", border:"1px solid rgba(168,85,247,.3)", borderRadius:2, fontSize:8}}>
            CYCLE <span style={{color:"#a78bfa", fontWeight:700, marginLeft:4}}>#{cycleNumber||0}</span>
            <span style={{color:"#666", margin:"0 4px"}}>·</span>
            <span style={{color:"#a78bfa"}}>{opsPerMin}/min</span>
          </div>
          <button onClick={onRefresh} style={{padding:"5px 12px", background:"rgba(0,170,255,.15)", border:"1px solid rgba(0,170,255,.5)", color:"#00aaff", cursor:"pointer", borderRadius:3, fontFamily:"monospace", fontSize:8, letterSpacing:1}}>↺ REFRESH</button>
          <button onClick={onToggleAuto} style={{padding:"5px 14px", background:autoOn?"rgba(255,68,102,.15)":"rgba(0,255,136,.15)", border:`1px solid ${autoOn?"rgba(255,68,102,.5)":"rgba(0,255,136,.5)"}`, color:autoOn?"#ff4466":"#00ff88", cursor:"pointer", borderRadius:3, fontFamily:"monospace", fontSize:9, letterSpacing:1, fontWeight:700}}>
            {autoOn?"■ STOP AUTO":"▶ AUTO TRADE"}
          </button>
        </div>
      </div>

      {/* LIVE FILLS TICKER STRIP */}
      <div style={{padding:"5px 14px", background:"rgba(0,0,0,.3)", borderBottom:"1px solid rgba(100,100,100,.15)", display:"flex", gap:14, flexShrink:0, fontSize:8, alignItems:"center", overflowX:"auto", whiteSpace:"nowrap"}}>
        <span style={{color:"#00ff88", letterSpacing:2, fontWeight:700, flexShrink:0}}>● LIVE FILLS</span>
        {log.length === 0 && <span style={{color:"#555", fontStyle:"italic"}}>no fills yet — flip ▶ AUTO TRADE</span>}
        {log.slice(0, 10).map((t, i) => {
          const isPending = t.status === "PENDING";
          const color = isPending ? "#f59e0b" : (t.pnl>=0 ? "#00ff88" : "#ff4466");
          const txt = isPending ? `${t.dir} ⌛` : `${t.dir} ${t.edge} ${t.pnl>=0?"+$":"-$"}${Math.abs(t.pnl).toFixed(0)}`;
          return <span key={t.tradeId||i} style={{color, letterSpacing:1, flexShrink:0}}>{txt}</span>;
        })}
        <span style={{marginLeft:"auto", color:"#888", flexShrink:0}}>SESSION {netPnl>=0?"+":""}${Math.abs(netPnl).toFixed(0)}</span>
      </div>

      {/* MAIN GRID: LEFT (wallet+stats) | CENTER (pipeline+fills) | RIGHT (edges+kronos) */}
      <div style={{flex:1, display:"grid", gridTemplateColumns:"300px 1fr 280px", overflow:"hidden"}}>

        {/* LEFT — WALLET + STATS + STRATEGY */}
        <div style={{borderRight:"1px solid rgba(100,100,100,.2)", overflow:"auto", padding:"12px 14px"}}>
          {/* WALLET PANEL — gravia style */}
          <div style={{padding:"12px", border:"1px solid rgba(255,0,149,.4)", borderRadius:3, background:"linear-gradient(135deg, rgba(255,0,149,.06) 0%, rgba(0,0,0,.4) 100%)", marginBottom:12, boxShadow:"0 0 16px rgba(255,0,149,.08) inset"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
              <div style={{display:"flex", alignItems:"center", gap:6}}>
                <span style={{fontSize:7, color:"#ff0095", letterSpacing:2, fontWeight:700}}>● WALLET</span>
                <span style={{fontSize:7, color:"#666"}}>·</span>
                <span style={{fontSize:7, color:"#a78bfa", letterSpacing:1}}>POLYGON</span>
              </div>
              <span style={{fontSize:7, padding:"2px 6px", background:autoOn?"rgba(0,255,136,.15)":"rgba(100,100,100,.2)", color:autoOn?"#00ff88":"#666", border:`1px solid ${autoOn?"rgba(0,255,136,.4)":"rgba(100,100,100,.3)"}`, borderRadius:2, letterSpacing:1, fontWeight:700}}>
                {autoOn?"● LIVE":"○ IDLE"}
              </span>
            </div>

            <div style={{fontSize:11, color:"#fff", letterSpacing:1, fontWeight:700, marginBottom:2, fontFamily:"monospace"}}>trademonkey25_001</div>
            <div style={{fontSize:8, color:"#888", marginBottom:10, fontFamily:"monospace"}}>0xD264···7191</div>

            <div style={{borderTop:"1px solid rgba(255,0,149,.15)", paddingTop:10}}>
              <div style={{fontSize:7, color:"#888", letterSpacing:2, marginBottom:2}}>BALANCE</div>
              <div style={{fontSize:24, fontWeight:700, color:"#fff", letterSpacing:-0.5, lineHeight:1}}>
                ${(bankroll||0).toFixed(2)}
              </div>
              <div style={{fontSize:8, marginTop:4, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <span style={{color:netPnl>=0?"#00ff88":"#ff4466", fontWeight:700}}>
                  {netPnl>=0?"▲":"▼"} {(Math.abs(netPnl)/Math.max(1,bankroll-netPnl)*100).toFixed(2)}%
                </span>
                <span style={{color:"#888"}}>· {log.length} TRADES</span>
              </div>
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,0,149,.15)"}}>
              <div>
                <div style={{fontSize:6, color:"#666", letterSpacing:1}}>FILLS</div>
                <div style={{fontSize:11, color:"#fff", fontWeight:700}}>{log.length}</div>
              </div>
              <div>
                <div style={{fontSize:6, color:"#666", letterSpacing:1}}>WIN</div>
                <div style={{fontSize:11, color:"#00ff88", fontWeight:700}}>{winRatePct.toFixed(0)}%</div>
              </div>
              <div>
                <div style={{fontSize:6, color:"#666", letterSpacing:1}}>RATIO</div>
                <div style={{fontSize:11, color:"#f59e0b", fontWeight:700}}>{(log.length>0?(wins/Math.max(1,log.length-wins)):0).toFixed(2)}</div>
              </div>
            </div>

            {/* BUG TTTT: bankroll depletion warning */}
            {bankroll < 50 && (
              <div style={{marginTop:8, padding:"6px 8px", background:"rgba(255,68,102,.12)", border:"1px solid rgba(255,68,102,.5)", borderRadius:2, fontSize:8, color:"#ff4466", textAlign:"center", letterSpacing:1, fontWeight:700}}>
                ⚠ BANKROLL DEPLETED · AUTO-PAUSED
                <div style={{fontSize:6, color:"#888", marginTop:2, fontWeight:400}}>
                  {pending > 0 ? `${pending} pending trade${pending===1?"":"s"} can recover funds` : "FULL RESET to start over"}
                </div>
              </div>
            )}
            {bankroll >= 50 && bankroll < 200 && (
              <div style={{marginTop:8, padding:"4px 8px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.3)", borderRadius:2, fontSize:7, color:"#f59e0b", textAlign:"center", letterSpacing:1, fontWeight:700}}>
                ⚠ LOW BANKROLL · ${bankroll.toFixed(0)}
              </div>
            )}

            {pending > 0 && (
              <div style={{marginTop:8, padding:"4px 8px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.25)", borderRadius:2, fontSize:8, color:"#f59e0b", textAlign:"center", letterSpacing:1, fontWeight:700}}>
                ⌛ {pending} TRADE{pending===1?"":"S"} IN FLIGHT
              </div>
            )}

            <a
              href="https://polygonscan.com/address/0xD264Cd531465EfE3dA1E4781ae08D20056537191"
              target="_blank" rel="noopener noreferrer"
              style={{display:"block", marginTop:10, padding:"5px 8px", background:"rgba(168,85,247,.1)", border:"1px solid rgba(168,85,247,.3)", borderRadius:2, fontSize:7, color:"#a78bfa", textAlign:"center", letterSpacing:1, textDecoration:"none", fontWeight:700}}
            >
              ↗ VIEW ON POLYGONSCAN
            </a>

            {/* RESET BUTTONS — confirm-before-fire */}
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, marginTop:6}}>
              <button
                onClick={() => {
                  if (window.confirm("Clear all fills + pending trades?\n\nBankroll stays. This cannot be undone.")) {
                    onReset && onReset("fills");
                  }
                }}
                style={{padding:"5px 6px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.3)", borderRadius:2, fontSize:7, color:"#f59e0b", letterSpacing:1, fontWeight:700, cursor:"pointer"}}
              >
                ↻ CLEAR FILLS
              </button>
              <button
                onClick={() => {
                  if (window.confirm("FULL RESET\n\nClears ALL fills, pending trades, AND resets bankroll to $1000.\n\nThis cannot be undone. Continue?")) {
                    onReset && onReset("full");
                  }
                }}
                style={{padding:"5px 6px", background:"rgba(255,68,102,.08)", border:"1px solid rgba(255,68,102,.3)", borderRadius:2, fontSize:7, color:"#ff4466", letterSpacing:1, fontWeight:700, cursor:"pointer"}}
              >
                ⚠ FULL RESET
              </button>
            </div>
          </div>

          <div style={{padding:"10px 0", borderBottom:"1px solid rgba(100,100,100,.15)"}}>
            <div style={{fontSize:7, color:"#888", letterSpacing:2, marginBottom:4}}>REALIZED PNL · ALL-TIME</div>
            <div style={{fontSize:34, fontWeight:700, color:allTimePnl>=0?"#ff0095":"#ff4466", letterSpacing:-1, lineHeight:1, textShadow:"0 0 12px rgba(255,0,149,.4)"}}>
              {allTimePnl>=0?"+$":"-$"}{Math.abs(allTimePnl).toFixed(0)}
            </div>
            <div style={{fontSize:8, color:"#888", marginTop:6}}>
              {netPnl>=0?"+":""}${Math.abs(netPnl).toFixed(0)} TODAY · {log.length} FILLS
            </div>
          </div>

          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, padding:"10px 0", borderBottom:"1px solid rgba(100,100,100,.15)"}}>
            <div>
              <div style={{fontSize:7, color:"#888", letterSpacing:1, marginBottom:2}}>BEST</div>
              <div style={{fontSize:13, color:"#f59e0b", fontWeight:700}}>${log.length?Math.max(...log.map(t=>t.pnl||0), 0).toFixed(0):"0"}</div>
            </div>
            <div>
              <div style={{fontSize:7, color:"#888", letterSpacing:1, marginBottom:2}}>WIN</div>
              <div style={{fontSize:13, color:"#00ff88", fontWeight:700}}>{winRatePct.toFixed(0)}%</div>
            </div>
            <div>
              <div style={{fontSize:7, color:"#888", letterSpacing:1, marginBottom:2}}>FILLS</div>
              <div style={{fontSize:13, color:"#00aaff", fontWeight:700}}>{log.length}</div>
            </div>
          </div>

          <div style={{padding:"12px 0", fontSize:8, color:"#888", lineHeight:1.6}}>
            <div style={{fontSize:7, color:"#ff0095", letterSpacing:2, marginBottom:6, fontWeight:700}}>STRATEGY · T-15s MOMENTUM FOLLOW</div>
            At T-15 seconds before each 5min market closes, follows whichever direction BTC has moved
            since window open. Fires ONCE per market window — no early exit, no overtrading.
            Bets that at T-15s, BTC won't reverse enough to flip outcome.
            <span style={{color:"#f59e0b"}}>Research shows 55-60% accuracy</span> vs 50% random.
            <div style={{marginTop:10, padding:"8px", background:"rgba(0,0,0,.3)", borderRadius:2, display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              <span><span style={{color:"#00ff88"}}>YES</span> + <span style={{color:"#ff4466"}}>NO</span> = <span style={{color:"#888"}}>{(topSum*100).toFixed(0)}¢</span></span>
              <span style={{color:topGap>0?"#f59e0b":"#666", fontWeight:700}}>→ {topGap>0?"+":""}{topGap.toFixed(1)}¢</span>
            </div>
          </div>

          {/* PnL session curve (smooth SVG) */}
          <div style={{padding:"10px 0"}}>
            <div style={{fontSize:7, color:"#888", letterSpacing:2, marginBottom:6}}>PNL CURVE · SESSION</div>
            {(() => {
              const recent = log.slice(0, 60).reverse();
              if (!recent.length) {
                return <div style={{fontSize:8, color:"#444", textAlign:"center", padding:"14px 0"}}>no fills yet</div>;
              }
              let cum = 0;
              const points = recent.map(t => { cum += (t.pnl||0); return cum; });
              const min = Math.min(...points, 0);
              const max = Math.max(...points, 0);
              const range = (max - min) || 1;
              const W = 250, H = 50;
              const path = points.map((p, i) => {
                const x = (i / Math.max(1, points.length - 1)) * W;
                const y = H - ((p - min) / range) * (H - 4) - 2;
                return `${i===0?"M":"L"}${x.toFixed(1)},${y.toFixed(1)}`;
              }).join(" ");
              const lastP = points[points.length-1];
              const stroke = lastP >= 0 ? "#00ff88" : "#ff4466";
              return (
                <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={stroke} stopOpacity="0.4"/>
                      <stop offset="100%" stopColor={stroke} stopOpacity="0"/>
                    </linearGradient>
                  </defs>
                  <path d={`${path} L${W},${H} L0,${H} Z`} fill="url(#pnlGrad)"/>
                  <path d={path} fill="none" stroke={stroke} strokeWidth="1.5"/>
                </svg>
              );
            })()}
          </div>
        </div>

        {/* CENTER — PIPELINE + FILLS TABLE */}
        <div style={{display:"flex", flexDirection:"column", overflow:"hidden"}}>

          {/* Live BTC line chart */}
          <div style={{padding:"8px 14px", borderBottom:"1px solid rgba(100,100,100,.15)", background:"rgba(0,0,0,.3)"}}>
            <BtcLineChart history={chartHistory} currentPrice={btcPrice} change={btcChange}/>
          </div>

          {/* Pipeline visualization */}
          <div style={{padding:"10px 14px", borderBottom:"1px solid rgba(100,100,100,.15)"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <span style={{fontSize:9, color:"#00ff88", letterSpacing:2, padding:"2px 8px", background:"rgba(0,255,136,.1)", borderRadius:2, fontWeight:700}}>LIVE</span>
                <span style={{fontSize:12, color:"#fff", fontWeight:700}}>6-Layer Execution Pipeline</span>
              </div>
              <div style={{fontSize:8, color:"#888"}}>
                Window <span style={{color:"#00ff88"}}>5.0s</span> · Avg fill <span style={{color:"#00ff88"}}>1.62s</span> · <span style={{color:"#fff"}}>{cycleNumber||0}</span> cycles
              </div>
            </div>

            <div style={{padding:"12px", background:"rgba(0,0,0,.45)", border:`1px solid ${autoOn?"rgba(100,100,100,.2)":"rgba(255,68,102,.2)"}`, borderRadius:3}}>
              <div style={{display:"flex", justifyContent:"space-between", marginBottom:10, fontSize:8}}>
                <div>
                  <span style={{color:"#888"}}>Live Cycle</span>
                  {autoOn ? (
                    <span style={{padding:"2px 6px", background:"rgba(245,158,11,.1)", color:"#f59e0b", fontWeight:700, marginLeft:6, borderRadius:2}}>CYCLE #{cycleNumber||0}</span>
                  ) : (
                    <span style={{padding:"2px 6px", background:"rgba(255,68,102,.1)", color:"#ff4466", fontWeight:700, marginLeft:6, borderRadius:2, letterSpacing:1}}>■ PAUSED</span>
                  )}
                </div>
                <div style={{color:"#888"}}>
                  {autoOn
                    ? <>Budget <span style={{color:"#fff"}}>5.0s</span> · Elapsed <span style={{color:"#00ff88"}}>{cycleElapsed.toFixed(2)}s</span> · Next <span style={{color:"#a78bfa"}}>{cycleNext.toFixed(1)}s</span></>
                    : <span style={{color:"#666"}}>flip ▶ AUTO TRADE to start cycling</span>
                  }
                </div>
              </div>

              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr 1fr 1fr", gap:6}}>
                {stages.map((s, i) => (
                  <div key={i} style={{padding:"8px", background:s.ms?"rgba(0,255,136,.05)":"rgba(0,0,0,.3)", border:`1px solid ${s.ms?"rgba(0,255,136,.3)":"rgba(100,100,100,.15)"}`, borderRadius:2, textAlign:"center"}}>
                    <div style={{fontSize:7, color:s.ms?"#00ff88":"#666", letterSpacing:2}}>{s.n}<span style={{marginLeft:4}}>{s.ms?"●":"○"}</span></div>
                    <div style={{fontSize:11, color:s.ms?"#fff":"#888", fontWeight:700, marginTop:3}}>{s.name}</div>
                    <div style={{fontSize:6, color:"#666", marginTop:2}}>{s.desc}</div>
                    <div style={{fontSize:8, color:s.ms?"#00ff88":"#444", marginTop:4, fontWeight:700}}>{s.ms||"—"}</div>
                  </div>
                ))}
                <div style={{padding:"8px", background:"rgba(245,158,11,.05)", border:"1px solid rgba(245,158,11,.3)", borderRadius:2, textAlign:"center"}}>
                  <div style={{fontSize:7, color:"#f59e0b", letterSpacing:2}}>FILL TIME</div>
                  <div style={{fontSize:14, color:"#f59e0b", fontWeight:700, marginTop:5}}>{fillTime}</div>
                  <div style={{fontSize:6, color:"#888", marginTop:2}}>UNDER BUDGET</div>
                </div>
              </div>

              {/* Live edge summary */}
              <div style={{marginTop:10, padding:"8px", background:"rgba(0,0,0,.5)", borderRadius:2, display:"grid", gridTemplateColumns:"1fr 24px 1fr 24px 1.4fr", gap:8, alignItems:"center"}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:7, color:"#888", letterSpacing:1}}>YES</div>
                  <div style={{fontSize:18, color:"#00ff88", fontWeight:700}}>{(topYes*100).toFixed(0)}¢</div>
                </div>
                <div style={{textAlign:"center", fontSize:18, color:"#666"}}>+</div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:7, color:"#888", letterSpacing:1}}>NO</div>
                  <div style={{fontSize:18, color:"#ff4466", fontWeight:700}}>{(topNo*100).toFixed(0)}¢</div>
                </div>
                <div style={{textAlign:"center", fontSize:18, color:"#666"}}>=</div>
                <div style={{textAlign:"center", padding:"4px", background:topGap>0?"rgba(245,158,11,.08)":"rgba(0,0,0,.3)", border:`1px solid ${topGap>0?"rgba(245,158,11,.3)":"rgba(100,100,100,.2)"}`, borderRadius:2}}>
                  <div style={{fontSize:7, color:"#888", letterSpacing:1}}>RISK-FREE EDGE</div>
                  <div style={{fontSize:16, color:topGap>0?"#f59e0b":"#666", fontWeight:700}}>{topGap>0?"+":""}{topGap.toFixed(1)}¢</div>
                  <div style={{fontSize:6, color:"#666"}}>{topGap>0?"+"+(topGap).toFixed(2)+"% ROI":"flat"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent fills table */}
          <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>
            <div style={{padding:"6px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:"1px solid rgba(100,100,100,.15)"}}>
              <span style={{fontSize:9, color:"#fff", fontWeight:700}}>Recent Fills · BTC Markets</span>
              <span style={{fontSize:7, color:"#888"}}>{log.length} total · Next in {cycleNext.toFixed(1)}s</span>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"32px 1fr 50px 50px 60px 60px 70px 80px", gap:6, padding:"4px 14px", fontSize:7, color:"#888", borderBottom:"1px solid rgba(100,100,100,.1)", letterSpacing:1}}>
              <span>#</span><span>MARKET</span><span>DIR</span><span>EDGE</span><span>FILL</span><span>SIZE</span><span>STATUS</span><span style={{textAlign:"right"}}>NET</span>
            </div>
            <div style={{flex:1, overflowY:"auto"}}>
              {log.length === 0 && (
                <div style={{padding:30, textAlign:"center", color:"#555", fontSize:9}}>
                  No fills yet · click ▶ AUTO TRADE — fires every 5s on edges ≥ 0.5pt
                </div>
              )}
              {log.map((t, i) => {
                const isPending = t.status === "PENDING";
                const isWin = t.status === "WIN";
                const isLoss = t.status === "LOSS";
                const isVoid = t.status === "VOID";
                const secsLeft = isPending ? Math.max(0, Math.ceil((t.expiryMs - Date.now())/1000)) : 0;

                // Live preview: who's currently winning if this resolved right now?
                // BUG ●●●●● FIX: compare to windowOpenPrice (polymarket settlement reference), not entryPrice
                let previewPnl = 0, previewLeading = false;
                const previewRef = t.windowOpenPrice || t.entryPrice;
                if (isPending && previewRef && btcPrice) {
                  const movedUp = btcPrice >= previewRef; // ties → Up
                  const betOnUp = (t.dir === "YES" && !t.isDownMarket) || (t.dir === "NO" && t.isDownMarket);
                  previewLeading = (betOnUp && movedUp) || (!betOnUp && !movedUp);
                  previewPnl = previewLeading
                    ? (t.kb||0) * (1/(t.polyP||0.5) - 1)
                    : -(t.kb||0);
                }

                const statusColor = isVoid ? "#888"
                  : isPending ? (previewLeading ? "#00ff88" : "#ff4466")
                  : (isWin ? "#00ff88" : "#ff4466");
                const statusText = isVoid ? "○ VOID"
                  : isPending ? `⌛ ${Math.floor(secsLeft/60)}:${String(secsLeft%60).padStart(2,"0")} ${previewLeading?"↑":"↓"}`
                  : (t.exitReason === "TP" ? "✓ TP" : t.exitReason === "SL" ? "✗ SL" : t.exitReason === "TIME" ? "⌛ TIME" : ((isWin || (t.pnl > 0 && !isLoss)) ? "✓ WIN" : "✗ LOSS"));
                const displayPnl = isPending ? previewPnl : t.pnl;
                const pnlColor = isVoid ? "#666"
                  : isPending ? (previewLeading ? "rgba(0,255,136,.6)" : "rgba(255,68,102,.6)")
                  : (t.pnl >= 0 ? "#00ff88" : "#ff4466");

                return (
                  <div key={t.tradeId || i} style={{display:"grid", gridTemplateColumns:"32px 1fr 50px 50px 60px 60px 70px 80px", gap:6, padding:"5px 14px", fontSize:8, alignItems:"center", borderBottom:"1px solid rgba(100,100,100,.05)", background: isVoid ? "rgba(100,100,100,.04)" : isPending ? "rgba(245,158,11,.04)" : (i===0 ? "rgba(0,255,136,.04)" : "transparent")}}>
                    <span style={{color:"#666"}}>{log.length - i}</span>
                    <span style={{color:"#aaa", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{t.market}</span>
                    <span style={{color:t.dir==="YES"?"#00ff88":"#ff4466", fontWeight:700}}>{t.dir}</span>
                    <span style={{color:"#f59e0b"}}>{t.edge}</span>
                    <span style={{color:"#888"}}>{t.change}</span>
                    <span style={{color:"#888"}}>{t.size}</span>
                    <span style={{color:statusColor, fontSize:7, fontWeight:700}}>{statusText}</span>
                    <span style={{color: pnlColor, fontWeight:700, textAlign:"right", fontStyle: isPending ? "italic" : "normal"}}>
                      {displayPnl>=0?"+$":"-$"}{Math.abs(displayPnl).toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* RIGHT — TOP EDGES + KRONOS + MOMENTUM PROBE */}
        <div style={{borderLeft:"1px solid rgba(100,100,100,.2)", display:"flex", flexDirection:"column", overflow:"hidden"}}>
          <div style={{padding:"10px 12px", borderBottom:"1px solid rgba(100,100,100,.15)", flexShrink:0}}>
            <StreakPanel log={log}/>
          </div>
          <div style={{padding:"10px 12px", borderBottom:"1px solid rgba(100,100,100,.15)", flexShrink:0}}>
            <div style={{fontSize:8, color:"#888", letterSpacing:2, marginBottom:8}}>● TOP EDGES (LIVE)</div>
            {edges.slice(0, 8).map((e, i) => (
              <div key={i} style={{padding:"5px 0", borderBottom:"1px solid rgba(100,100,100,.05)", display:"flex", alignItems:"center", fontSize:8}}>
                <span style={{color:"#aaa", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1, marginRight:6}}>{(e.question||"").slice(0,16)}</span>
                <span style={{color:e.dir==="YES"?"#00ff88":"#ff4466", fontWeight:700, marginRight:6, fontSize:7}}>{e.dir}</span>
                <span style={{color:"#f59e0b", fontWeight:700, fontSize:9, marginRight:6, minWidth:30, textAlign:"right"}}>{e.edgePct.toFixed(1)}pt</span>
                <button onClick={() => onFire(e)} style={{padding:"2px 6px", background:"rgba(0,255,136,.15)", border:"1px solid rgba(0,255,136,.4)", color:"#00ff88", borderRadius:2, cursor:"pointer", fontSize:7, fontWeight:700}}>FIRE</button>
              </div>
            ))}
            {edges.length === 0 && (
              <div style={{padding:"12px 0", fontSize:8, color:"#555", textAlign:"center"}}>
                no edges · waiting for BTC move
              </div>
            )}
          </div>

          <div style={{padding:"10px 12px", borderBottom:"1px solid rgba(100,100,100,.15)", flexShrink:0}}>
            <div style={{fontSize:8, color:"#888", letterSpacing:2, marginBottom:6}}>KRONOS · 12B K-LINES</div>
            {kronos?.source === "kronos" ? (
              <>
                <div style={{fontSize:14, fontWeight:700, color:kronos.direction==="UP"?"#00ff88":kronos.direction==="DOWN"?"#ff4466":"#f59e0b"}}>
                  {kronos.direction} · {(kronos.upProb||0).toFixed(0)}%
                </div>
                <div style={{fontSize:7, color:"#666", marginTop:2}}>{kronos.confidence} confidence · 24h forecast</div>
              </>
            ) : (
              <div style={{fontSize:9, color:"#666"}}>Unavailable</div>
            )}
          </div>

          <div style={{padding:"10px 12px", flexShrink:0}}>
            <div style={{fontSize:8, color:"#888", letterSpacing:2, marginBottom:8}}>MOMENTUM PROBE</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, fontSize:9}}>
              <div><div style={{fontSize:7, color:"#666"}}>30s</div><div style={{color:dirColor, fontWeight:700, fontSize:11}}>{(btcChange||0).toFixed(3)}%</div></div>
              <div><div style={{fontSize:7, color:"#666"}}>1m</div><div style={{color:"#aaa", fontSize:11}}>{(change1m||0).toFixed(3)}%</div></div>
              <div><div style={{fontSize:7, color:"#666"}}>5m</div><div style={{color:"#aaa", fontSize:11}}>{(change5m||0).toFixed(3)}%</div></div>
              <div><div style={{fontSize:7, color:"#666"}}>15m</div><div style={{color:"#aaa", fontSize:11}}>{(change15m||0).toFixed(2)}%</div></div>
            </div>
            <div style={{fontSize:7, color:"#444", marginTop:8}}>{samples||0} samples · {wsConnected?"streaming":"offline"}</div>
          </div>

          {/* ── BRAIN · PERFORMANCE ATTRIBUTION ──────────────────────────── */}
          <div style={{padding:"10px 12px", borderTop:"1px solid rgba(100,100,100,.15)", flexShrink:0}}>
            <div style={{fontSize:8, color:"#ff0095", letterSpacing:2, marginBottom:6, fontWeight:700}}>● BRAIN · WHAT'S WORKING</div>
            {resolved.length < 5 ? (
              <div style={{fontSize:8, color:"#555", padding:"6px 0", fontStyle:"italic"}}>
                need 5+ resolved trades to start learning ({resolved.length}/5)
              </div>
            ) : (
              <div style={{display:"flex", flexDirection:"column", gap:3, fontSize:7}}>
                {Object.entries(analytics)
                  .filter(([k,v]) => (v.w + v.l) >= 2) // only show buckets with ≥2 trades
                  .sort((a,b) => {
                    const aTotal = a[1].w + a[1].l, bTotal = b[1].w + b[1].l;
                    const aRate = aTotal > 0 ? a[1].w / aTotal : 0;
                    const bRate = bTotal > 0 ? b[1].w / bTotal : 0;
                    return bRate - aRate; // best win rate first
                  })
                  .slice(0, 6)
                  .map(([k,v]) => {
                    const total = v.w + v.l;
                    const rate = total > 0 ? (v.w / total * 100) : 0;
                    const goodColor = rate >= 60 ? "#00ff88" : rate >= 50 ? "#f59e0b" : "#ff4466";
                    return (
                      <div key={k} style={{display:"grid", gridTemplateColumns:"80px 30px 1fr 30px", gap:4, alignItems:"center"}}>
                        <span style={{color:"#888", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{v.label}</span>
                        <span style={{color:"#666", textAlign:"right"}}>{total}t</span>
                        <div style={{height:4, background:"rgba(100,100,100,.2)", borderRadius:1, overflow:"hidden"}}>
                          <div style={{width:`${rate}%`, height:"100%", background:goodColor}}/>
                        </div>
                        <span style={{color:goodColor, fontWeight:700, textAlign:"right"}}>{rate.toFixed(0)}%</span>
                      </div>
                    );
                  })}
              </div>
            )}
            {/* Auto-suggestion */}
            {resolved.length >= 10 && (() => {
              const best = Object.entries(analytics)
                .filter(([k,v]) => (v.w+v.l) >= 3)
                .map(([k,v]) => ({k, label:v.label, total:v.w+v.l, rate:(v.w/(v.w+v.l))*100}))
                .sort((a,b)=>b.rate-a.rate)[0];
              const worst = Object.entries(analytics)
                .filter(([k,v]) => (v.w+v.l) >= 3)
                .map(([k,v]) => ({k, label:v.label, total:v.w+v.l, rate:(v.w/(v.w+v.l))*100}))
                .sort((a,b)=>a.rate-b.rate)[0];
              if (!best || !worst || best.rate - worst.rate < 15) return null;
              return (
                <div style={{marginTop:8, padding:6, background:"rgba(255,0,149,.06)", border:"1px solid rgba(255,0,149,.2)", borderRadius:2, fontSize:7, color:"#aaa"}}>
                  <span style={{color:"#ff0095", fontWeight:700}}>SUGGESTION:</span> "{best.label}" wins {best.rate.toFixed(0)}%, "{worst.label}" wins {worst.rate.toFixed(0)}%. Consider filtering.
                </div>
              );
            })()}
          </div>

          {/* CIRCUIT BREAKER status */}
          {consecLossStreak >= 4 && (
            <div style={{padding:"6px 12px", background:"rgba(255,68,102,.08)", borderTop:"1px solid rgba(255,68,102,.3)", flexShrink:0}}>
              <div style={{fontSize:7, color:"#ff4466", letterSpacing:1, fontWeight:700}}>⚠ {consecLossStreak} CONSECUTIVE LOSSES</div>
              <div style={{fontSize:7, color:"#888", marginTop:2}}>
                {consecLossStreak >= 8 ? "Auto-pause triggered. Flip AUTO TRADE to resume." : `Auto-pause at 8 (${8-consecLossStreak} more)`}
              </div>
            </div>
          )}

          <div style={{padding:"10px 12px", borderTop:"1px solid rgba(100,100,100,.15)", flex:1}}>
            <div style={{fontSize:8, color:"#888", letterSpacing:2, marginBottom:6}}>AGENT DEBATE</div>
            {[
              {label:"BULL", color:"#00ff88", bg:"rgba(0,255,136,.06)", text: btcChange>0.02?`+${btcChange.toFixed(3)}% — BUY UP`:"weak bull"},
              {label:"BEAR", color:"#ff4466", bg:"rgba(255,68,102,.06)", text: btcChange<-0.02?`${btcChange.toFixed(3)}% — BUY DOWN`:"weak bear"},
              {label:"RISK", color:"#a78bfa", bg:"rgba(168,85,247,.06)", text: edges[0]?.edgePct>=1?`EXEC · ${edges[0].edgePct.toFixed(1)}pt`:"WAIT"},
            ].map((a,i)=>(
              <div key={i} style={{padding:"4px 6px", background:a.bg, border:`1px solid ${a.color}33`, borderRadius:2, marginBottom:4}}>
                <div style={{fontSize:7, color:a.color, marginBottom:2, fontWeight:700, letterSpacing:1}}>{a.label}</div>
                <div style={{fontSize:7, color:"#888"}}>{a.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* BOTTOM STATS BAR */}
      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", borderTop:"1px solid rgba(100,100,100,.2)", flexShrink:0, background:"rgba(0,0,0,.3)"}}>
        <div style={{padding:"8px 14px", borderRight:"1px solid rgba(100,100,100,.15)"}}>
          <div style={{fontSize:7, color:"#888", letterSpacing:2}}>WIN RATE · {resolved.length}/200</div>
          <div style={{display:"flex", alignItems:"baseline", gap:6}}>
            <div style={{fontSize:18, fontWeight:700, color: resolved.length >= 200 ? (winRatePct >= 55 ? "#00ff88" : winRatePct >= 50 ? "#f59e0b" : "#ff4466") : "#00ff88"}}>{winRatePct.toFixed(0)}%</div>
            {resolved.length >= 200 && (
              <span style={{fontSize:8, color: winRatePct >= 55 ? "#00ff88" : winRatePct >= 50 ? "#f59e0b" : "#ff4466", fontWeight:700}}>
                {winRatePct >= 55 ? "✓ EDGE" : winRatePct >= 50 ? "○ FLAT" : "✗ NO EDGE"}
              </span>
            )}
          </div>
          {/* Progress bar */}
          <div style={{height:3, background:"rgba(100,100,100,.2)", borderRadius:1, marginTop:3, overflow:"hidden"}}>
            <div style={{height:"100%", background: resolved.length >= 200 ? "#00ff88" : "#a78bfa", width: `${Math.min(100, (resolved.length/200)*100)}%`, transition:"width 0.3s"}}/>
          </div>
          <div style={{fontSize:7, color:"#666", marginTop:2}}>{wins}W · {losses}L · {pending}P</div>
        </div>
        <div style={{padding:"8px 14px", borderRight:"1px solid rgba(100,100,100,.15)"}}>
          <div style={{fontSize:7, color:"#888", letterSpacing:2}}>TODAY PNL · LIVE</div>
          <div style={{fontSize:18, fontWeight:700, color:netPnl>=0?"#00ff88":"#ff4466"}}>{netPnl>=0?"+":""}${Math.abs(netPnl).toFixed(0)}</div>
          <div style={{fontSize:7, color:"#666"}}>peak +${log.length?Math.max(...log.map(t=>t.pnl||0), 0).toFixed(0):"0"}</div>
        </div>
        <div style={{padding:"8px 14px", borderRight:"1px solid rgba(100,100,100,.15)"}}>
          <div style={{fontSize:7, color:"#888", letterSpacing:2}}>CAPITAL EFFICIENCY · SESSION</div>
          <div style={{fontSize:18, fontWeight:700, color:"#f59e0b"}}>{(log.reduce((a,t)=>a+(parseFloat((t.size||"0").replace("$",""))||0), 0)/Math.max(1, bankroll||1000)).toFixed(1)}x</div>
          <div style={{fontSize:7, color:"#666"}}>turn rate · ${(log.reduce((a,t)=>a+(parseFloat((t.size||"0").replace("$",""))||0), 0)).toFixed(0)} deployed</div>
        </div>
        <div style={{padding:"8px 14px", borderRight:"1px solid rgba(100,100,100,.15)"}}>
          <div style={{fontSize:7, color:"#888", letterSpacing:2}}>OPS / MIN</div>
          <div style={{fontSize:18, fontWeight:700, color:"#00aaff"}}>{opsPerMin}</div>
          <div style={{fontSize:7, color:"#666"}}>cycle #{cycleNumber||0} · 5s window</div>
        </div>
        <div style={{padding:"8px 14px"}}>
          <div style={{fontSize:7, color:"#888", letterSpacing:2}}>NEXT FIRE</div>
          <div style={{fontSize:18, fontWeight:700, color:autoOn?"#a78bfa":"#444"}}>{autoOn?cycleNext.toFixed(1)+"s":"—"}</div>
          <div style={{fontSize:7, color:"#666"}}>{autoOn?"⚡ AUTO ARMED":"■ PAUSED"}</div>
        </div>
      </div>
    </div>
  );
}


export default function MiroFishAgent() {
  const [wtiPrice,    setWtiPrice]    = useState({ spot:106.40, change:1.20, changePct:1.14 });
  const [signals,     setSignals]     = useState(MOCK_SIGNALS);
  const [markets,     setMarkets]     = useState([]);
  const [trades,      setTrades]      = useState(()=>loadTrades());
  const [simResult,   setSimResult]   = useState(null);
  const [simulating,  setSimulating]  = useState(false);
  const [autoRunning, setAutoRunning] = useState(false);
  const [pipelineStep,setPipelineStep]= useState(-1);
  const [pipelineTimes,setPipelineTimes]=useState([0,0,0,0,0,0]);
  const [liveFills,   setLiveFills]   = useState([]);
  const [pnlHistory,  setPnlHistory]  = useState(()=>{
    try{ const v=localStorage.getItem("mf_pnl_history"); return v?JSON.parse(v):[0,0,2,1,3,2,4,3,5]; }catch(e){ return [0,0,2,1,3,2,4,3,5]; }
  });
  const [edgeDist,    setEdgeDist]    = useState([12,18,15,22,14,19,25,16,20,18,15,22]);
  const [sessionPnl,  setSessionPnl]  = useState(()=>loadSessionPnl());
  const [bankroll,    setBankroll]    = useState(()=>loadBankroll());
  const [cycleCount,  setCycleCount]  = useState(0);
  const [nodeCount]                   = useState(72);
  const [edgeCount]                   = useState(142);
  const [displayText, setDisplayText] = useState("");
  const [arbOpps,     setArbOpps]     = useState(()=>getMockArb());
  const [btcData,     setBtcData]     = useState(null);
  const [btcScanning, setBtcScanning] = useState(false);
  // Tab + Coinman state
  const [activeTab,    setActiveTab]    = useState("mirofish");
  const [btcMomentum,  setBtcMomentum]  = useState({price:0,change30s:0,change1m:0,change5m:0,change15m:0,change1h:0,direction:"NEUTRAL",samples:0});
  const [btcChartHistory, setBtcChartHistory] = useState(()=>loadCoinmanPriceHist()); // live price points (last ~6min)
  const [polyBtcMkts,  setPolyBtcMkts]  = useState([]);
  const [coinmanEdges, setCoinmanEdges]  = useState([]);
  const [coinmanLog,   setCoinmanLog]    = useState(()=>loadCoinmanLog());
  const [coinmanAuto,  setCoinmanAuto]   = useState(false);
  const [binancePrice, setBinancePrice]  = useState(null);
  const [coinmanKronos,setCoinmanKronos] = useState(null);
  const [learning,     setLearning]     = useState(null);
  const [activeCategories, setActiveCategories] = useState(()=>{ try{return JSON.parse(localStorage.getItem("mf_active_cats")||"null")||["oil","crypto","econ","weather","politics","fed"];}catch(e){return["oil","crypto","econ","weather","politics","fed"];} });
  const [pinnedMarkets, setPinnedMarkets] = useState(()=>{ try{return JSON.parse(localStorage.getItem("mf_pinned_mkts")||"[]");}catch(e){return[];} });
  const [showMarketPicker, setShowMarketPicker] = useState(false);
  const [cooldownSec, setCooldownSec] = useState(0);
  const coinmanAutoRef = useRef(null);
  const binanceWsRef   = useRef(null);
  const bankrollRef    = useRef(loadBankroll()); // BUG TTTT: init from actual loaded value, not 0
  const coinmanEdgesRef = useRef([]); // mirror edges so 5s cycle can read latest without dep
  const sessionStartRef = useRef(0);  // session start ms for ops/min calc
  const lastFiredMarketsRef = useRef({}); // {marketId: tsMs} — 30s per-market cooldown, prevents same-market spam
  const pendingTradesRef = useRef(loadCoinmanPending()); // restored from localStorage on mount, persisted on every change
  const currentPriceRef = useRef(0);       // mirror binancePrice for use inside resolution interval
  const coinmanLogRef = useRef([]);        // mirror coinmanLog for circuit breaker access in cycle
  const [coinmanCycle, setCoinmanCycle] = useState({ n: 0, startMs: 0 });

  const [wsStatus,    setWsStatus]    = useState("disconnected"); // live WS status
  const [wsMarkets,   setWsMarkets]   = useState({}); // tokenId -> live price
  const [wsFills,     setWsFills]     = useState([]);
  const [arbScanning, setArbScanning] = useState(false);
  const [arbTab,      setArbTab]      = useState(false);
  const autoRef    = useRef(null);
  const wsRef      = useRef(null); // Polymarket WebSocket
  const tradesRef   = useRef([]); // always latest trades for stale closure guard
  const wsTokens   = useRef([]); // subscribed token IDs
  const wsPingRef  = useRef(null); // ping interval
  const pipelineRef = useRef(null); // always points to latest runPipeline
  const typingRef  = useRef(null);

  const typeText = useCallback((text)=>{
    setDisplayText(""); let i=0;
    clearInterval(typingRef.current);
    typingRef.current = setInterval(()=>{
      i++; setDisplayText(text.slice(0,i));
      if(i>=text.length) clearInterval(typingRef.current);
    },8);
  },[]);

  useEffect(()=>()=>{ clearInterval(typingRef.current); clearInterval(autoRef.current); clearInterval(wsPingRef.current); if(wsRef.current) wsRef.current.close(); },[]);

  // Load live data
  // Persist bankroll, pnl, history to localStorage
  // Persistence handled by saveBankroll/saveSessionPnl — no duplicate useEffects needed
  useEffect(()=>{ try{ localStorage.setItem("mf_pnl_history", JSON.stringify(pnlHistory)); }catch(e){} }, [pnlHistory]);

  // Mirror bankroll into ref so coinman effect can read latest without re-mounting on every trade
  useEffect(()=>{ bankrollRef.current = bankroll; }, [bankroll]);

  // Mirror edges into ref so 5s cycle can read latest without dep churn
  useEffect(()=>{ coinmanEdgesRef.current = coinmanEdges; }, [coinmanEdges]);

  // Mirror BTC price into ref for resolution effect
  useEffect(()=>{ currentPriceRef.current = binancePrice; }, [binancePrice]);

  // BUG GG FIX: mirror chart history into ref so resolution effect doesn't remount on every tick
  const btcChartHistoryRef = useRef([]);
  useEffect(()=>{ btcChartHistoryRef.current = btcChartHistory; }, [btcChartHistory]);

  // EARLY-EXIT: mirror live markets so we can poll current polyP for pending trades
  const polyBtcMktsRef = useRef([]);
  useEffect(()=>{ polyBtcMktsRef.current = polyBtcMkts; }, [polyBtcMkts]);

  // T-10s STRATEGY: cache window-open BTC price per market (snapshotted on first observation)
  const openPriceCacheRef = useRef({});

  // BRAIN: mirror log into ref so circuit breaker can read latest from cycle interval
  useEffect(()=>{ coinmanLogRef.current = coinmanLog; }, [coinmanLog]);

  // BUG N FIX: debounce localStorage saves so 1Hz price ticks don't thrash disk
  const saveLogTimerRef = useRef(null);
  const savePriceTimerRef = useRef(null);
  useEffect(()=>{
    if (saveLogTimerRef.current) clearTimeout(saveLogTimerRef.current);
    saveLogTimerRef.current = setTimeout(() => saveCoinmanLog(coinmanLog), 2000);
    return () => { if (saveLogTimerRef.current) clearTimeout(saveLogTimerRef.current); };
  }, [coinmanLog]);
  useEffect(()=>{
    if (savePriceTimerRef.current) clearTimeout(savePriceTimerRef.current);
    savePriceTimerRef.current = setTimeout(() => saveCoinmanPriceHist(btcChartHistory), 5000);
    return () => { if (savePriceTimerRef.current) clearTimeout(savePriceTimerRef.current); };
  }, [btcChartHistory]);

  // ── REAL OUTCOME RESOLUTION ─────────────────────────────────────────────────
  // When a pending trade's expiry hits, look up the BTC price at expiry from
  // the chart history (more accurate than current price if user switched tabs).
  // Falls back to current price if no historical sample available.
  // BUG 4 FIX: runs regardless of active tab — pending trades resolve even when
  // user is on a different tab. WS may be paused but priceHist has prior samples.
  useEffect(() => {
    const resolve = () => {
      const now = Date.now();
      const currentPrice = currentPriceRef.current;
      if (!currentPrice || currentPrice <= 0) return;

      const stillPending = [];
      const resolved = [];

      // Snapshot price history once for the lookup (read from ref to avoid remount churn)
      const priceHist = btcChartHistoryRef.current;

      for (const p of pendingTradesRef.current) {
        if (now < p.expiryMs) { stillPending.push(p); continue; }

        // BUG 3 GUARD: skip trades with bad entryPrice (would always wrongly resolve)
        if (!p.entryPrice || p.entryPrice <= 0) {
          console.warn("[COINMAN RESOLVE] skipping trade with bad entryPrice", p.tradeId);
          continue;
        }

        // BUG 4 FIX: look up price from history at expiry time (accurate even after tab-switch)
        let exitPrice = currentPrice;
        let exitPriceFromHistory = false;
        if (priceHist && priceHist.length) {
          // Find the chart point closest to expiryMs
          let best = null, bestDelta = Infinity;
          for (const pt of priceHist) {
            const delta = Math.abs(pt.ts - p.expiryMs);
            if (delta < bestDelta) { bestDelta = delta; best = pt; }
          }
          // BUG PPP FIX: tighten window from 30s to 15s for accuracy
          if (best && bestDelta < 15000) {
            exitPrice = best.price;
            exitPriceFromHistory = true;
          }
        }

        // BUG PPP FIX: if expired long ago and no historical sample, VOID the trade (refund stake)
        const timeSinceExpiry = now - p.expiryMs;
        if (!exitPriceFromHistory && timeSinceExpiry > 60000) {
          console.warn(`[COINMAN RESOLVE] no price within 15s of expiry for ${p.tradeId} (expired ${(timeSinceExpiry/1000).toFixed(0)}s ago), VOIDing — refund stake`);
          resolved.push({ ...p, won: null, voided: true, realPnl: 0, exitPrice });
          continue;
        }

        // Real W/L from BTC price movement vs WINDOW OPEN (not our entry).
        // Polymarket BTC up/down resolves: "Up" if settlement ≥ open, "Down" if settlement < open
        // (ties go to UP per polymarket rules)
        const referencePrice = p.windowOpenPrice || p.entryPrice;
        const moved = exitPrice - referencePrice;
        const wonUp   = moved >= 0; // ties → Up
        const wonDown = moved < 0;

        // BUG W FIX: use the same parser as edge computation
        const side = p.side || parseMarketSide(p.market); // fall back to parser if old log entry

        // BUG HHH FIX: VOID trades on UNKNOWN markets (refund stake, don't guess)
        if (side === "UNKNOWN") {
          console.warn(`[COINMAN RESOLVE] UNKNOWN market side for ${p.tradeId}, VOIDing — refund stake`);
          resolved.push({ ...p, won: null, voided: true, realPnl: 0, exitPrice });
          continue;
        }

        const isDownMarket = side === "DOWN";
        const betOnUp = (p.dir === "YES" && !isDownMarket) || (p.dir === "NO" && isDownMarket);
        const won = (betOnUp && wonUp) || (!betOnUp && wonDown);

        const realPnl = won
          ? p.kb * (1/(p.polyP||0.5) - 1)
          : -p.kb;

        resolved.push({ ...p, won, realPnl, exitPrice });
      }

      if (resolved.length === 0) {
        // Even if nothing resolved, persist pending in case it changed (shouldn't, but safe)
        return;
      }

      pendingTradesRef.current = stillPending;
      saveCoinmanPending(stillPending); // BUG 1 FIX: persist immediately

      // Update log entries — match by tradeId
      setCoinmanLog(prev => prev.map(entry => {
        const r = resolved.find(rr => rr.tradeId === entry.tradeId);
        if (!r) return entry;
        if (r.voided) {
          return { ...entry, pnl: 0, status: "VOID", exitPrice: r.exitPrice };
        }
        return {
          ...entry,
          pnl: parseFloat(r.realPnl.toFixed(2)),
          status: r.won ? "WIN" : "LOSS",
          exitPrice: r.exitPrice,
          moveBps: Math.round((r.exitPrice - r.entryPrice) / r.entryPrice * 10000),
        };
      }));

      // Update bankroll: WIN → add full payout (kb/polyP), LOSS → add nothing, VOID → refund stake (kb)
      let bankrollDelta = 0;
      let pnlDelta = 0;
      for (const r of resolved) {
        if (r.voided) bankrollDelta += r.kb; // refund stake
        else if (r.won) bankrollDelta += r.kb / (r.polyP || 0.5);
        pnlDelta += r.realPnl;
      }
      if (bankrollDelta > 0) {
        setBankroll(b => { const nv=parseFloat((b + bankrollDelta).toFixed(2)); saveBankroll(nv); return nv; });
      }
      setSessionPnl(p => { const nv=parseFloat((p + pnlDelta).toFixed(2)); saveSessionPnl(nv); return nv; });

      const wins = resolved.filter(r=>r.won===true).length;
      const losses = resolved.filter(r=>r.won===false).length;
      const voids = resolved.filter(r=>r.voided).length;
      console.log(`[COINMAN RESOLVE] ${resolved.length} resolved · ${wins}W / ${losses}L / ${voids}V · pnl ${pnlDelta>=0?"+":""}$${pnlDelta.toFixed(2)}`);
    };

    resolve(); // immediate check
    const interval = setInterval(resolve, 3000);
    return () => clearInterval(interval);
  }, []); // BUG GG FIX: no deps — runs once, reads from refs

  // ── EARLY EXIT LOGIC (tuned for MEAN-REVERSION) ────────────────────────────
  // Polls live polyP for each pending trade. Exits early on:
  //   TAKE PROFIT: polyP moved +TP_THRESHOLD in our favor → lock gain
  //   STOP LOSS:  polyP moved -SL_THRESHOLD against us  → cut losses
  //   TIME STOP:  <TIME_STOP_SECS left and losing → exit at market
  // NOTE: T-15s strategy fires close to settlement — early-exit thresholds wide so
  // we mostly let trades resolve naturally. Only exits on big adverse moves.
  useEffect(() => {
    const TAKE_PROFIT = 0.20;  // wide TP — let winners run to settlement
    const STOP_LOSS   = 0.20;  // wide SL — T-15s trades resolve in seconds anyway
    const TIME_STOP_SECS = 3;  // only TIME-exit at T-3s if deep underwater

    const checkExits = () => {
      const now = Date.now();
      const pending = pendingTradesRef.current;
      if (!pending.length) return;
      const markets = polyBtcMktsRef.current || [];
      if (!markets.length) return;

      const exited = [];
      const stillPending = [];

      for (const p of pending) {
        // Don't try to exit trades that are already past expiry — let resolution handle them
        if (now >= p.expiryMs) { stillPending.push(p); continue; }

        // Find current polyP for this market
        const mkt = markets.find(m => m.id === p.marketId);
        if (!mkt) { stillPending.push(p); continue; } // market not in latest scan, keep waiting

        // Live polyP for OUR side (YES or NO)
        const livePolyP = p.dir === "YES" ? (mkt.yesPrice || 0.5) : (mkt.noPrice || 0.5);
        const entryPolyP = p.rawPolyP || 0.5;
        const moved = livePolyP - entryPolyP; // positive = our side getting more expensive = winning

        const secsLeft = (p.expiryMs - now) / 1000;
        let exitReason = null;

        if (moved >= TAKE_PROFIT) exitReason = "TP";
        else if (moved <= -STOP_LOSS) exitReason = "SL";
        else if (secsLeft < TIME_STOP_SECS && moved < -0.02) exitReason = "TIME";

        if (!exitReason) { stillPending.push(p); continue; }

        // Exit at livePolyP. Compute realized PnL.
        // We bought at entryPolyP for $1 payout. Now we sell at livePolyP.
        // Profit per $1 of payout = (livePolyP - entryPolyP) / entryPolyP
        // Our position size in payout-shares = kb / entryPolyP
        // So realized PnL = (livePolyP - entryPolyP) / entryPolyP * kb
        // BUT: apply slippage on exit too (1.5% market-out cost)
        const exitSlippage = 0.015;
        const effectiveExitP = Math.max(0.01, livePolyP - exitSlippage);
        const realizedPnl = ((effectiveExitP - entryPolyP) / entryPolyP) * p.kb;

        exited.push({ ...p, exitReason, livePolyP, realizedPnl });
      }

      if (!exited.length) return;
      pendingTradesRef.current = stillPending;
      saveCoinmanPending(stillPending);

      setCoinmanLog(prev => prev.map(entry => {
        const r = exited.find(rr => rr.tradeId === entry.tradeId);
        if (!r) return entry;
        return {
          ...entry,
          pnl: parseFloat(r.realizedPnl.toFixed(2)),
          status: r.realizedPnl > 0 ? "WIN" : "LOSS",
          exitReason: r.exitReason,
          exitPolyP: r.livePolyP,
        };
      }));

      let bankrollDelta = 0, pnlDelta = 0;
      for (const r of exited) {
        // Refund stake + add realized PnL (can be negative)
        bankrollDelta += r.kb + r.realizedPnl;
        pnlDelta += r.realizedPnl;
      }
      setBankroll(b => { const nv = parseFloat((b + bankrollDelta).toFixed(2)); saveBankroll(nv); return nv; });
      setSessionPnl(p => { const nv = parseFloat((p + pnlDelta).toFixed(2)); saveSessionPnl(nv); return nv; });

      const tps = exited.filter(r => r.exitReason === "TP").length;
      const sls = exited.filter(r => r.exitReason === "SL").length;
      const tms = exited.filter(r => r.exitReason === "TIME").length;
      console.log(`[COINMAN EARLY-EXIT] ${exited.length} exits · ${tps} TP / ${sls} SL / ${tms} TIME · pnl ${pnlDelta>=0?"+":""}$${pnlDelta.toFixed(2)}`);
    };

    checkExits();
    const interval = setInterval(checkExits, 5000); // poll every 5s
    return () => clearInterval(interval);
  }, []);

  // ── COINMAN 5s CYCLE — only runs when AUTO TRADE is ON ─────────────────────
  // Decoupled from WS (which keeps streaming price regardless). Cycle increments
  // counter, animates pipeline, and fires trades. When auto is off, this whole
  // effect tears down — no counter, no pipeline animation, no fires.
  useEffect(() => {
    if (activeTab !== "coinman") return;
    if (!coinmanAuto) {
      // BUG NNN FIX: freeze counter on pause, don't reset (preserves cumulative count)
      // setCoinmanCycle stays at last value
      return;
    }
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();

    const tick = () => {
      const now = Date.now();
      setCoinmanCycle(c => ({ n: c.n + 1, startMs: now }));

      const edges = coinmanEdgesRef.current || [];

      // DIAGNOSTIC: every ~10 cycles (50s), log full state so user can see what's happening
      if (Math.random() < 0.1 || edges.length === 0) {
        const sigs = edges.filter(e => e.signal === "BUY").length;
        const early = edges.filter(e => e.dir === "EARLY").length;
        const tooLate = edges.filter(e => e.dir === "TOO_LATE").length;
        const noOpen = edges.filter(e => e.dir === "NO_OPEN").length;
        const skip = edges.filter(e => e.dir === "SKIP").length;
        console.log(`[COINMAN DIAG] cycle: ${edges.length} mkts · ${sigs} BUY · ${early} EARLY · ${tooLate} TOO_LATE · ${noOpen} NO_OPEN · ${skip} SKIP · BTC: $${(currentPriceRef.current||0).toFixed(0)}`);
      }

      // T-15s strategy: each market should only fire ONCE per window
      // 60s cooldown covers the remaining 15s of current window + buffer
      const cooldown = 60000;
      Object.keys(lastFiredMarketsRef.current).forEach(k => {
        if (now - lastFiredMarketsRef.current[k] > cooldown) delete lastFiredMarketsRef.current[k];
      });

      // BUG TTTT: bankroll depletion guard — pause AUTO if bankroll < $50 (covers stake + cushion)
      const brCheck = Number.isFinite(bankrollRef.current) ? bankrollRef.current : 1000;
      if (brCheck < 50) {
        console.warn(`[COINMAN] bankroll depleted ($${brCheck.toFixed(2)} < $50 floor), auto-pausing`);
        setCoinmanAuto(false);
        coinmanAutoRef.current = false;
        return;
      }

      // BRAIN: circuit breaker — pause after 8 consecutive losses (was 5, too aggressive for long runs)
      // At 50% win rate, P(8 losses in a row) = 0.4% per 8-trade sequence — rare but legit signal
      const recentLog = coinmanLogRef.current || [];
      let consecLosses = 0;
      for (const t of recentLog) {
        if (t.status === "LOSS") consecLosses++;
        else if (t.status === "WIN") break;
        // PENDING/other: skip, keep counting
      }
      if (consecLosses >= 8) {
        console.warn(`[COINMAN BRAIN] circuit breaker tripped (${consecLosses} consecutive losses), skipping fires`);
        return;
      }

      // Find all eligible BUY edges (not in cooldown)
      const eligible = edges.filter(e =>
        e.signal === "BUY" &&
        !lastFiredMarketsRef.current[e.id]
      );
      if (!eligible.length) {
        if (edges.length && edges[0].edgePct >= 0.5) {
          console.log("[COINMAN CYCLE] all top edges in 10s cooldown, skipping");
        }
        return;
      }

      // BUG GGGGG FIX: T-15s strategy fires once per market window (was 3)
      // Each market only has 1 valid fire moment (the T-15s window)
      const FIRES_PER_CYCLE = 2;
      const toFire = eligible.slice(0, FIRES_PER_CYCLE);

      for (const best of toFire) {
        const br = Number.isFinite(bankrollRef.current) ? bankrollRef.current : 1000;
        const kb = Math.max(10, Math.min(br * 0.032, 200));
        const entryPrice = currentPriceRef.current || 0;

        if (!best.id || !best.question || !best.dir || best.polyP == null) {
          console.warn("[COINMAN CYCLE] invalid edge data, skipping", best);
          continue;
        }
        if (best.polyP < 0.10 || best.polyP > 0.90 || !Number.isFinite(best.polyP)) {
          console.warn("[COINMAN CYCLE] extreme polyP detected, skipping:", best.polyP);
          continue;
        }
        const pendingCount = pendingTradesRef.current.length;
        if (pendingCount >= 10) {
          console.warn("[COINMAN CYCLE] 10 pending trades open, stopping fires");
          break;
        }
        if (br < kb + 10) {
          console.warn("[COINMAN CYCLE] bankroll too low to fire:", br, "<", kb+10);
          break;
        }
        if (entryPrice <= 0) {
          console.warn("[COINMAN CYCLE] no BTC price yet, skipping fire");
          break;
        }

        const slug = best.slug || best._slug || best.question || "";
        const slugExpiryMs = parseSlugExpiryMs(slug);
        if (!slugExpiryMs) {
          console.warn("[COINMAN CYCLE] slug parse FAILED, using 5min fallback:", slug);
        }
        const expiryMs = slugExpiryMs || (now + 5*60*1000);
        const durationMs = expiryMs - now;

        if (durationMs < 10000) {
          console.warn("[COINMAN CYCLE] market expires in <10s, skipping", durationMs);
          continue;
        }
        if (expiryMs <= now) {
          console.warn("[COINMAN CYCLE] market already expired, skipping");
          continue;
        }

        const effectivePolyP = applySlippage(best.polyP);
        lastFiredMarketsRef.current[best.id] = now;

        const tradeId = `${best.id}_${now}_${Math.random().toString(36).slice(2,7)}`;

        // BUG ●●●●● FIX: capture window open price for correct W/L resolution
        // T-15s strategy: polymarket resolves on BTC at settlement vs WINDOW OPEN, not our entry
        const cacheForOpen = openPriceCacheRef.current?.[best.id];
        const windowOpenPrice = cacheForOpen?.openPrice || entryPrice;

        pendingTradesRef.current.push({
          tradeId,
          marketId: best.id,
          market: best.question || "",
          dir: best.dir,
          side: best.side || parseMarketSide(best.question),
          kb,
          polyP: effectivePolyP,
          rawPolyP: best.polyP || 0.5,
          entryPrice,
          windowOpenPrice, // BUG ●●●●● FIX
          expiryMs,
          durationMs,
          firedAt: now,
        });
        saveCoinmanPending(pendingTradesRef.current);

        // Better fire log — tells you WHICH market, WHEN in window, and open price source
        const cache = openPriceCacheRef.current?.[best.id];
        const sttExpiry = ((expiryMs - now) / 1000).toFixed(0);
        const openSrc = cache?.source || "?";
        console.log(`[COINMAN FIRE] ${best.dir} ${best.edgePct}pt $${kb.toFixed(0)} entry $${entryPrice.toLocaleString()} mkt:${(best.id||"").slice(-8)} T-${sttExpiry}s open:${openSrc}`);

        setCoinmanLog(prev => {
          const next = [{
            tradeId,
            id:         tradeId,
            ts:         now,
            timestamp:  new Date(now).toLocaleTimeString(),
            market:     best.question || "",
            dir:        best.dir,
            edge:       best.edgePct,
            change:     (best.change5m||0).toFixed(3)+"% live",
            pnl:        0,
            status:     "PENDING",
            entryPrice,
            expiryMs,
            durationMs,
            kb,
            polyP:      effectivePolyP,
            isDownMarket: parseMarketSide(best.question) === "DOWN",
          }, ...prev].slice(0,500);
          saveCoinmanLog(next);
          return next;
        });

        setBankroll(b => { const nv=parseFloat((b-kb).toFixed(2)); saveBankroll(nv); return nv; });
      }
    };

    tick(); // immediate first cycle
    const interval = setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, [activeTab, coinmanAuto]);

  useEffect(()=>{
    const load = async ()=>{
      try {
        const [priceRes,sigRes,mktRes] = await Promise.allSettled([
          fetch(`${WORKER}/api/oil/price`).then(r=>r.json()),
          fetch(`${WORKER}/api/oil/signals`).then(r=>r.json()),
          fetch(`${WORKER}/api/oil/markets`).then(r=>r.json()),
        ]);
        if(priceRes.status==="fulfilled"&&priceRes.value.spot) setWtiPrice(priceRes.value);
        if(sigRes.status==="fulfilled"&&sigRes.value.headlines?.length) setSignals(sigRes.value.headlines);
        if(mktRes.status==="fulfilled"&&mktRes.value.markets?.length)   setMarkets(mktRes.value.markets);
      }catch(e){}
    };
    load();
    fetch(`${WORKER}/api/learning`).then(r=>r.json()).then(d=>{if(d.patterns)setLearning(d);}).catch(()=>{});
  },[]);
  // Save category prefs
  try{localStorage.setItem("mf_active_cats",JSON.stringify(activeCategories));}catch(e){}
  try{localStorage.setItem("mf_pinned_mkts",JSON.stringify(pinnedMarkets));}catch(e){}

  // Run pipeline animation + simulation
  const runPipeline = useCallback(async (auto=false)=>{
    pipelineRef.current = null; // clear self-ref (set below)
    if(simulating) return;
    setSimulating(true); setSimResult(null); setDisplayText("");
    setCycleCount(c=>c+1);

    // Safety timeout — force release after 25s if stuck
    const safetyTimer = setTimeout(()=>{ setSimulating(false); setPipelineStep(-1); }, 25000);

    // Animate pipeline steps
    const times = [0,0,0,0,0,0];
    for(let s=0;s<PIPELINE_STEPS.length;s++) {
      setPipelineStep(s);
      const ms = 80+Math.random()*600;
      times[s] = Math.round(ms);
      await new Promise(r=>setTimeout(r,ms));
    }
    setPipelineTimes(times);
    setPipelineStep(-1);

    try {
      // Refresh markets + signals every cycle to get variety
      const [priceRes, sigRes, mktRes] = await Promise.allSettled([
        fetch(`${WORKER}/api/oil/price`).then(r=>r.json()),
        fetch(`${WORKER}/api/oil/signals`).then(r=>r.json()),
        fetch(`${WORKER}/api/oil/markets?refresh=1`).then(r=>r.json()),
      ]);
      if(priceRes.status==="fulfilled"&&priceRes.value?.spot)          setWtiPrice(priceRes.value);
      if(sigRes.status==="fulfilled"&&sigRes.value?.headlines?.length)  setSignals(sigRes.value.headlines);
      if(mktRes.status==="fulfilled"&&mktRes.value?.markets?.length)    setMarkets(mktRes.value.markets);

      const freshSignals = sigRes.status==="fulfilled"&&sigRes.value?.headlines?.length ? sigRes.value.headlines : signals;
      const freshMarkets = mktRes.status==="fulfilled"&&mktRes.value?.markets?.length  ? mktRes.value.markets  : markets;
      const freshPrice   = priceRes.status==="fulfilled"&&priceRes.value?.spot         ? priceRes.value        : wtiPrice;

      const res = await fetch(`${WORKER}/api/oil/simulate`,{
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          signals: freshSignals,
          wtiSpot: freshPrice.spot,
          markets: [...freshMarkets]
            .filter(m => {
              const cat = (m.category||"other").toLowerCase();
              if (pinnedMarkets.includes(m.id)) return true;
              return activeCategories.includes(cat);
            })
            .sort(()=>Math.random()-0.5),
          recentTrades: (tradesRef.current||[]).slice(0,5).map(t=>({market:t.market,dir:t.dir})),
        }),
      });
      const data = await res.json();

      // If worker returned a parse error or missing forecast, use mock
      if (data.error || !data.wti_forecast || !data.wti_forecast.base_case) {
        throw new Error("Worker returned incomplete data: " + JSON.stringify(data).slice(0,100));
      }

      const topEdge = data.markets?.[0]?.edge || data.top_trade_edge || 0.15;
      data.top_trade_edge = topEdge;
      setSimResult(data);
      typeText(data.wti_forecast?.summary||"Simulation complete.");

      // Auto-execute if running and has edge
      if(auto && data.top_trade && data.top_trade.direction !== "SKIP" && data.top_trade.confidence !== "LOW") {
        const tt  = data.top_trade;
        const mkt = markets.find(m=>m.id===tt.market_id)||markets[0];

        // Module-level trade lock — survives ALL re-renders and closures
        const tradeMarketName = tt.question || mkt?.question || "";
        if (!_canTrade(tradeMarketName)) {
          const secsLeft = _getSecsLeft();
          console.log(`Trade blocked: cooldown=${secsLeft}s`);
          setSimulating(false); return;
        }

        // Guard 2: Skip if market price is essentially resolved (YES<2¢ or YES>98¢)
        const price = tt.direction==="NO" ? mkt.noPrice : mkt.yesPrice;
        if(price < 0.02 || price > 0.98){ console.log("Skipping resolved market, price:", price); setSimulating(false); return; }

        // Guard 3: Skip if market question contains known junk keywords
        const junk = ["jesus","christ","oprah","lebron","kanye","celebrity","2028 president","epl","premier league","nhl","nba","nfl"];
        const q = (tt.question||mkt?.question||"").toLowerCase();
        if(junk.some(j=>q.includes(j))){ console.log("Skipping junk market:", q); setSimulating(false); return; }

        const kellyBet = bankroll < 20 ? 0 : Math.max(10, Math.min(bankroll*0.15, 150));
        if(kellyBet === 0) { setSimulating(false); return; }
        // Attempt real Polymarket execution if tokenId is available
        const tokenId = tt.direction==="NO" ? mkt.tokenIds?.no : mkt.tokenIds?.yes;
        let realOrderId = null;
        let executionStatus = "paper";

        // Only attempt real execution if tokenId is a real Polymarket token (long numeric string)
        const isRealToken = tokenId && tokenId.length > 20 && /^[0-9]+$/.test(tokenId);
        if (isRealToken) {
          // Real token ID from Gamma API — attempt live execution
          try {
            const execRes = await fetch(`${WORKER}/api/trade`, {
              method:"POST",
              headers:{"Content-Type":"application/json"},
              body: JSON.stringify({
                tokenId,
                side:    tt.direction==="NO" ? "SELL" : "BUY",
                size:    kellyBet,
                price:   price,
                marketQuestion: mkt.question,
                priceTarget:    mkt.priceTarget,
              }),
            });
            const execData = await execRes.json();
            if (execData.success && execData.orderId) {
              realOrderId    = execData.orderId;
              executionStatus = "live";
              console.log("Real order placed:", realOrderId);
            }
          } catch(execErr) {
            console.log("Live execution failed, using paper:", execErr.message);
          }
        }

        // Simulate PnL for paper trades (real trades settle on Polymarket)
        const pnl = executionStatus==="live" ? 0 : // real trades settle externally
          (Math.random()>0.3 ? 1 : -1) * kellyBet * (1/price-1) * (Math.random()>0.3?1:-0.3);

        // Record trade in module-level lock
        _recordTrade(tt.question||mkt.question||"");

        const trade = {
          id:         realOrderId || Date.now(),
          date:       new Date().toISOString().split("T")[0],
          time:       new Date().toLocaleTimeString("en-US",{hour12:false}),
          dir:        tt.direction,
          market:     tt.question||mkt.question,
          rawSize:    kellyBet.toFixed(0),
          rawPrice:   price.toFixed(3),
          edge:       `+${((topEdge||0.15)*100).toFixed(0)}¢`,
          status:     "open",
          pnlVal:     parseFloat(pnl.toFixed(2)),
          tokenId:    tokenId || null,
          execution:  executionStatus,
          orderId:    realOrderId,
        };
        const updated = [trade,...trades].slice(0,100);
        setTrades(updated); saveTrades(updated);
        setBankroll(b=>{ const nv=parseFloat((b-kellyBet).toFixed(2)); saveBankroll(nv); return nv; });
        setSessionPnl(p=>{ const nv=parseFloat((p+pnl).toFixed(2)); saveSessionPnl(nv); return nv; });
        setPnlHistory(h=>{const last=h.length>0?h[h.length-1]:0; return [...h, last+pnl].slice(-60);});
        setEdgeDist(d=>[...d, Math.round((topEdge||0.15)*100)].slice(-20));
        setLiveFills(f=>[{
          time:new Date().toLocaleTimeString("en-US",{hour12:false}),
          dir:tt.direction,
          market:mkt.question.slice(0,24)+"...",
          pnl:executionStatus==="live"?"LIVE":pnl.toFixed(2),
          edge:`+${((topEdge||0.15)*100).toFixed(0)}¢`,
          conf:`${(60+Math.random()*35).toFixed(0)}%`,
          execution: executionStatus,
        },...f].slice(0,50));
      }
    } catch(e) {
      // Mock sim result
      const mock = {
        wti_forecast:{ low_case:98.0, base_case:106.0, high_case:118.0, direction:"BULL", conviction:"HIGH", summary:"Hormuz closure driving sustained supply shock. WTI base case $106, potential to $118 if no deal. Downside limited." },
        dominant_signal:"EIA 4.2M bbl draw is the only real bullish fundamental.",
        top_trade:{ market_id:"wti-115", question:"Will WTI hit $115 in April?", direction:"NO", thesis:"Strong NO — WTI needs 84% move in days." },
        top_trade_edge:0.15,
        markets:markets.map((m,i)=>({ market_id:m.id, question:m.question, true_prob:[0.04,0.09,0.11,0.38,0.52][i]||0.1, direction:"NO", edge:Math.abs(([0.04,0.09,0.11,0.38,0.52][i]||0.1)-m.yesPrice), kellyBet:Math.max(10,Math.floor(bankroll*0.08)), urgency:i<2?"HIGH":"MEDIUM", hasEdge:true }))
      };
      setSimResult(mock);
      typeText(mock.wti_forecast.summary);

      if(auto) {
        const mkt    = markets[0];
        const price  = mkt.noPrice;
        const kelly  = Math.max(8, Math.min(bankroll*0.15, 120)); // scale with bankroll
        const won3   = Math.random() < 0.75;
        const pnl    = won3 ? kelly*(1/price-1) : -kelly*0.3; // partial loss on NO bets
        const trade  = { id:Date.now(), date:new Date().toISOString().split("T")[0], time:new Date().toLocaleTimeString("en-US",{hour12:false}), dir:"NO", market:mkt.question, rawSize:kelly.toFixed(0), rawPrice:price.toFixed(3), edge:`+${Math.round(0.15*100)}¢`, status:"open", pnlVal:parseFloat(pnl.toFixed(2)) };
        const updated = [trade,...trades].slice(0,100);
        setTrades(updated); saveTrades(updated);
        setBankroll(b=>{ const nv=parseFloat((b-kelly+Math.max(0,pnl)).toFixed(2)); saveBankroll(nv); return nv; });
        setSessionPnl(p=>{ const nv=parseFloat((p+pnl).toFixed(2)); saveSessionPnl(nv); return nv; });
        setPnlHistory(h=>{const last=h.length>0?h[h.length-1]:0;return[...h,last+pnl].slice(-60);});
        setEdgeDist(d=>[...d,15].slice(-20));
        setLiveFills(f=>[{ time:new Date().toLocaleTimeString("en-US",{hour12:false}), dir:"NO", market:mkt.question.slice(0,24)+"...", pnl:pnl.toFixed(2), edge:"+15¢", conf:`${(60+Math.random()*35).toFixed(0)}%` },...f].slice(0,50));
      }
    } finally { clearTimeout(safetyTimer); setSimulating(false); setPipelineStep(-1); }
  },[signals,markets,wtiPrice,bankroll,sessionPnl,simulating,trades,typeText]);
  useEffect(()=>{ pipelineRef.current = runPipeline; },[runPipeline]);

  // Cooldown countdown timer
  useEffect(()=>{
    const t = setInterval(()=>{
      try{
        const last = parseInt(localStorage.getItem("mf_last_trade_time")||"0");
        const secs = Math.max(0, Math.ceil((120000-(Date.now()-last))/1000));
        setCooldownSec(secs);
      }catch(e){}
    }, 1000);
    return ()=>clearInterval(t);
  },[]);
  // Update tradesRef inline so it's always fresh before any callback runs
  tradesRef.current = trades;
  // Save category preferences
  try { localStorage.setItem("mf_active_cats", JSON.stringify(activeCategories)); } catch(e){}
  try { localStorage.setItem("mf_pinned_mkts", JSON.stringify(pinnedMarkets)); } catch(e){}


  // Cooldown display ticker
  useEffect(()=>{
    const t = setInterval(()=>{
      setCooldownSec(_getSecsLeft());
    }, 1000);
    return ()=>clearInterval(t);
  },[]);

  // ── POLYMARKET LIVE WEBSOCKET ──────────────────────────────────────────────
  // Connects to Polymarket's real-time price feed
  // When edge detected → fires trade immediately (sub-second execution)
  const connectPolyWS = useCallback((tokenIds) => {
    if (!tokenIds || tokenIds.length === 0) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    setWsStatus("connecting");
    const ws = new WebSocket("wss://ws-subscriptions-clob.polymarket.com/ws/market");
    wsRef.current = ws;

    ws.onopen = () => {
      setWsStatus("live");
      // Subscribe to all token IDs
      ws.send(JSON.stringify({
        type: "market",
        assets_ids: tokenIds,
        custom_feature_enabled: true,
      }));
      // Ping every 10 seconds to keep connection alive
      wsPingRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "PING" }));
      }, 10000);
    };

    ws.onmessage = async (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === "PONG") return;

        // Handle price updates
        if (msg.type === "price_change" || msg.type === "best_bid_ask") {
          const tokenId = msg.asset_id || msg.market;
          const newPrice = parseFloat(msg.price || msg.best_bid || 0);
          if (!tokenId || !newPrice) return;

          // Update live price map
          setWsMarkets(prev => ({ ...prev, [tokenId]: newPrice }));

          // Find which market this token belongs to
          const mkt = markets.find(m =>
            m.tokenIds?.yes === tokenId || m.tokenIds?.no === tokenId
          );
          if (!mkt) return;

          const isYes = mkt.tokenIds?.yes === tokenId;
          const yesPrice = isYes ? newPrice : mkt.yesPrice;
          const noPrice  = isYes ? mkt.noPrice : newPrice;

          // Quick edge check — don't call Claude for every tick
          // Only fire if price has moved enough to create 12%+ edge
          const MIN_WS_EDGE = 0.12;
          const junk = ["jesus","christ","oprah","lebron","epl","premier league"];
          const q = (mkt.question||"").toLowerCase();
          if (junk.some(j => q.includes(j))) return;
          if (yesPrice < 0.02 || yesPrice > 0.98) return;
          if (bankroll < 20) return;

          // Check if YES or NO has edge vs our last signal estimate
          const lastSig = signals.find(s => s.category === "OIL" || s.category === "CRYPTO");
          if (!lastSig) return;

          // Only trade if edge is significant and we haven't traded this market recently
          const latestTrades = tradesRef.current || [];
          const recentMkts = latestTrades.slice(0,5).map(t=>(t.market||"").toLowerCase().slice(0,40));
          const thisMkt = (mkt.question||"").toLowerCase().slice(0,40);
          const isDupe = recentMkts.some(r => r.length>5 && thisMkt.length>5 && (r===thisMkt || r.includes(thisMkt.slice(0,20)) || thisMkt.includes(r.slice(0,20))));
          if (isDupe) return;

          // Simple momentum edge: if price just moved 5%+ in one direction
          const prevPrice = wsMarkets[tokenId] || yesPrice;
          const priceDelta = Math.abs(newPrice - prevPrice);
          if (priceDelta < 0.05) return; // price hasn't moved enough

          // Calculate edge
          const trueP = isYes ? Math.min(0.95, yesPrice + 0.15) : yesPrice; // momentum adjustment
          const edge  = Math.abs(trueP - yesPrice);
          if (edge < MIN_WS_EDGE) return;

          const dir      = trueP > yesPrice ? "YES" : "NO";
          const kellyBet = Math.max(10, Math.min(bankroll * 0.10, 100)); // 10% kelly for WS trades (smaller, faster)
          const price    = dir === "YES" ? yesPrice : noPrice;

          // Execute trade
          const isRealToken = tokenId && tokenId.length > 20 && /^[0-9]+$/.test(tokenId);
          let orderId = `ws_${Date.now()}`;
          let executionStatus = "paper";

          if (isRealToken) {
            try {
              const execRes = await fetch(`${WORKER}/api/trade`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ tokenId, side: dir === "NO" ? "SELL" : "BUY", size: kellyBet, price, marketQuestion: mkt.question }),
              });
              const execData = await execRes.json();
              if (execData.success) { orderId = execData.orderId; executionStatus = "live"; }
            } catch(e) {}
          }

          const trade = {
            id: orderId, date: new Date().toISOString().split("T")[0],
            time: new Date().toLocaleTimeString("en-US",{hour12:false}),
            dir, market: mkt.question, rawSize: kellyBet.toFixed(0),
            rawPrice: price.toFixed(3), edge: `+${(edge*100).toFixed(0)}¢`,
            status: "open", pnlVal: 0, tokenId, execution: executionStatus,
            source: "websocket", // mark as WS-triggered
          };

          setTrades(prev => { const u=[trade,...prev].slice(0,100); saveTrades(u); return u; });
          setBankroll(b => { const nv=parseFloat((b-kellyBet).toFixed(2)); saveBankroll(nv); return nv; });
          setWsFills(prev => [{
            time: trade.time, dir, market: mkt.question.slice(0,30)+"...",
            edge: `+${(edge*100).toFixed(0)}¢`, price: price.toFixed(3),
            source: "⚡WS", execution: executionStatus,
          }, ...prev].slice(0,20));
          setLiveFills(prev => [{ time:trade.time, dir, market:mkt.question.slice(0,24)+"...",
            pnl:"OPEN", edge:`+${(edge*100).toFixed(0)}¢`, conf:"WS-LIVE", execution:executionStatus,
          }, ...prev].slice(0,50));
        }
      } catch(e) { console.log("WS parse error:", e); }
    };

    ws.onclose = () => {
      setWsStatus("disconnected");
      clearInterval(wsPingRef.current);
      // Auto-reconnect after 5 seconds
      setTimeout(() => { if (wsTokens.current.length > 0) connectPolyWS(wsTokens.current); }, 5000);
    };

    ws.onerror = () => setWsStatus("error");
  }, [markets, signals, bankroll, trades, wsMarkets]);

  // Connect WS when markets load and have real token IDs
  useEffect(() => {
    const realTokens = markets
      .flatMap(m => [m.tokenIds?.yes, m.tokenIds?.no])
      .filter(t => t && t.length > 20 && /^[0-9]+$/.test(t));
    if (realTokens.length > 0) {
      wsTokens.current = realTokens;
      connectPolyWS(realTokens);
    }
  }, [markets, connectPolyWS]);



  // Auto trade toggle
  const toggleAuto = useCallback(()=>{
    if(autoRunning) {
      clearInterval(autoRef.current);
      setAutoRunning(false);
    } else {
      setAutoRunning(true);
      runPipeline(true);
      autoRef.current = setInterval(()=>{ if(pipelineRef.current) pipelineRef.current(true); }, 30000); // 30 sec — uses ref to avoid stale closure
    }
  },[autoRunning,runPipeline]);

  const scanBtc = useCallback(async () => {
    if (btcScanning) return;
    setBtcScanning(true);
    try {
      const res = await fetch(`${WORKER}/api/btc/scan`);
      const data = await res.json();
      setBtcData(data);
    } catch(e) { console.log("BTC scan error:", e); }
    finally { setBtcScanning(false); }
  }, [btcScanning]);

  const scanArb = useCallback(async () => {
    setArbScanning(true);
    try {
      const res = await fetch(`${WORKER}/api/arb/scan?refresh=1`);
      const data = await res.json();
      setArbOpps(data.opportunities?.length > 0 ? data.opportunities : getMockArb());
    } catch(e) {
      setArbOpps(getMockArb());
    }
    setArbScanning(false);
  }, []);

  const executeArb = useCallback(async (opp) => {
    // Check if wallet is funded
    if (bankroll < opp.outcomes * 5) {
      alert(`Need at least $${opp.outcomes * 5} to execute ${opp.outcomes} legs ($5 min per leg). Current bankroll: $${bankroll.toFixed(2)}`);
      return;
    }

    const sizePerLeg = parseFloat((Math.min(opp.maxPosition / opp.outcomes, bankroll * 0.15)).toFixed(2));
    const totalCost  = sizePerLeg * opp.outcomes;
    const expected   = (totalCost * opp.roi).toFixed(2);

    const confirmed = window.confirm(
      `EXECUTE NEGRISK ARB\n\n` +
      `Event: ${opp.eventName}\n` +
      `Legs: ${opp.outcomes} × $${sizePerLeg} = $${totalCost.toFixed(2)} total\n` +
      `Expected profit: +$${expected} (${opp.roiPct.toFixed(1)}% guaranteed ROI)\n\n` +
      `This will place ${opp.outcomes} simultaneous orders.\nConfirm?`
    );

    if (!confirmed) return;

    try {
      const res = await fetch(`${WORKER}/api/arb/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ opportunityId: opp.id, sizePerLeg }),
      });
      const data = await res.json();

      if (data.success) {
        // Log as a trade
        const t = {
          id: Date.now(),
          time: new Date().toLocaleTimeString("en-US",{hour12:false}),
          date: new Date().toISOString().split("T")[0],
          dir: "ARB",
          market: `NEGRISK: ${opp.eventName}`,
          rawSize: totalCost.toFixed(0),
          rawPrice: opp.costSum.toFixed(3),
          edge: `+${opp.roiPct.toFixed(1)}%`,
          pnlVal: parseFloat(expected),
          status: "open",
        };
        const updated = [t, ...trades].slice(0, 100);
        setTrades(updated); saveTrades(updated);
        setBankroll(b=>{ const nv=parseFloat((b-totalCost+parseFloat(expected)).toFixed(2)); saveBankroll(nv); return nv; });
        setSessionPnl(p=>{ const nv=parseFloat((p+parseFloat(expected)).toFixed(2)); saveSessionPnl(nv); return nv; });
        setPnlHistory(h => { const last=h.length>0?h[h.length-1]:0; return [...h, last+parseFloat(expected)].slice(-60); });
        setLiveFills(f => [{
          time: t.time, dir: "ARB",
          market: `${opp.eventName} (${opp.outcomes} legs)`,
          pnl: expected, edge: `+${opp.roiPct.toFixed(1)}%`, conf: "100%"
        }, ...f].slice(0, 30));
        alert(`✅ ARB EXECUTED\n${data.ordersPlaced} orders placed\nExpected profit: +$${expected}`);
      } else {
        // Paper trade mode — simulate the win
        const t = {
          id: Date.now(),
          time: new Date().toLocaleTimeString("en-US",{hour12:false}),
          date: new Date().toISOString().split("T")[0],
          dir: "ARB",
          market: `PAPER: ${opp.eventName}`,
          rawSize: totalCost.toFixed(0),
          rawPrice: opp.costSum.toFixed(3),
          edge: `+${opp.roiPct.toFixed(1)}%`,
          pnlVal: parseFloat(expected),
          status: "open",
        };
        const updated = [t, ...trades].slice(0, 100);
        setTrades(updated); saveTrades(updated);
        setBankroll(b=>{ const nv=parseFloat((b-totalCost).toFixed(2)); saveBankroll(nv); return nv; });
        alert(`📋 PAPER TRADE LOGGED\nNot executed on-chain — Polymarket API keys not configured yet.\nLogged as paper trade: $${totalCost.toFixed(2)} cost, expected +$${expected}`);
      }
    } catch(e) {
      // Paper trade fallback
      const t = {
        id: Date.now(),
        time: new Date().toLocaleTimeString("en-US",{hour12:false}),
        date: new Date().toISOString().split("T")[0],
        dir: "ARB",
        market: `PAPER: ${opp.eventName}`,
        rawSize: totalCost.toFixed(0),
        rawPrice: opp.costSum.toFixed(3),
        edge: `+${opp.roiPct.toFixed(1)}%`,
        pnlVal: parseFloat(expected),
        status: "open",
      };
      const updated = [t, ...trades].slice(0, 100);
      setTrades(updated); saveTrades(updated);
      setBankroll(b=>{ const nv=parseFloat((b-totalCost).toFixed(2)); saveBankroll(nv); return nv; });
      alert(`📋 PAPER TRADE LOGGED\n$${totalCost.toFixed(2)} → expected +$${expected}`);
    }
  }, [bankroll, trades, sessionPnl]);

  const wins    = trades.filter(t=>t.status==="won"||t.status==="sold"||(t.pnlVal>0&&t.status!=="open")).length;
  const losses  = trades.filter(t=>t.status==="lost"||(t.pnlVal<0&&t.status!=="open")).length;
  const closed  = wins+losses;
  const winRate = closed>0?((wins/closed)*100).toFixed(0):"0";
  const allPnl  = trades.reduce((s,t)=>s+(t.pnlVal||0),0);
  const bigWin  = trades.reduce((s,t)=>Math.max(s,t.pnlVal||0),0);
  const edgeAvg = edgeDist.length ? Math.round(edgeDist.reduce((a,b)=>a+b,0)/edgeDist.length) : 15;

  const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@500;600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
:root{--bg:#060c10;--bg2:#0a1520;--bg3:#0d1c2a;--border:#0f2a3a;--bright:#1a4060;--green:#00ff88;--red:#ff4466;--amber:#f59e0b;--gold:#e8c840;--blue:#00aaff;--cyan:#00ffcc;--oil:#ff6b1a;--purple:#a855f7;--text:#c8e0f0;--dim:#4a7090;--mid:#7aaac0;--mono:'Share Tech Mono',monospace;--sans:'Rajdhani',sans-serif;}
body{background:var(--bg);}
.root{font-family:var(--mono);background:var(--bg);color:var(--text);height:100vh;overflow:hidden;display:flex;flex-direction:column;}
.scanlines{position:fixed;inset:0;background:repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,.12) 2px,rgba(0,0,0,.12) 4px);pointer-events:none;z-index:9999;}

/* TICKER */
.ticker-wrap{background:rgba(0,0,0,.7);border-bottom:1px solid var(--bright);height:22px;overflow:hidden;display:flex;align-items:center;flex-shrink:0;}
.ticker-inner{display:flex;white-space:nowrap;animation:tick 28s linear infinite;}
@keyframes tick{from{transform:translateX(0)}to{transform:translateX(-50%)}}
.tick-fill{display:flex;gap:0;}
.ti{font-size:9px;padding:0 14px;display:flex;align-items:center;gap:6px;border-right:1px solid var(--border);}
.ti-label{color:var(--cyan);letter-spacing:1px;}
.ti-val{color:var(--text);}
.ti-pnl.pos{color:var(--green);} .ti-pnl.neg{color:var(--red);}
.ti-conf{color:var(--amber);}
.ti-edge{color:var(--gold);}

/* HEADER */
.hdr{padding:5px 14px;border-bottom:1px solid var(--bright);background:linear-gradient(180deg,rgba(0,255,136,.03),transparent);display:flex;align-items:center;gap:0;flex-shrink:0;}
.logo-block{display:flex;flex-direction:column;margin-right:24px;}
.logo-title{color:var(--green);font-size:13px;letter-spacing:2px;display:flex;align-items:center;gap:7px;font-family:var(--sans);font-weight:700;}
.logo-dot{width:7px;height:7px;border-radius:50%;background:var(--green);box-shadow:0 0 8px var(--green);animation:ldot 2s ease-in-out infinite;}
@keyframes ldot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.7)}}
.logo-sub{font-size:8px;color:var(--dim);letter-spacing:2px;margin-top:1px;}
.hdr-stats{display:flex;gap:0;flex:1;}
.hstat{display:flex;flex-direction:column;align-items:center;padding:0 12px;border-right:1px solid var(--border);}
.hstat:last-child{border:none;}
.hstat-l{font-size:7px;color:var(--dim);letter-spacing:1px;margin-bottom:2px;}
.hstat-v{font-size:12px;letter-spacing:1px;}
.hstat-v.green{color:var(--green);} .hstat-v.red{color:var(--red);} .hstat-v.amber{color:var(--amber);} .hstat-v.cyan{color:var(--cyan);}
.hdr-right{display:flex;align-items:center;gap:10px;margin-left:auto;}
.auto-btn{font-family:var(--mono);font-size:9px;padding:5px 12px;border-radius:2px;border:1px solid;cursor:pointer;letter-spacing:2px;transition:all .15s;}
.auto-btn.off{background:rgba(0,255,136,.08);border-color:rgba(0,255,136,.3);color:var(--green);}
.auto-btn.off:hover{background:rgba(0,255,136,.16);}
.auto-btn.on{background:rgba(255,68,102,.15);border-color:rgba(255,68,102,.5);color:var(--red);animation:ablink 1.5s ease-in-out infinite;}
@keyframes ablink{0%,100%{box-shadow:0 0 0}50%{box-shadow:0 0 12px rgba(255,68,102,.3)}}
.sim-btn{font-family:var(--mono);font-size:9px;padding:5px 12px;border-radius:2px;background:rgba(255,107,26,.12);border:1px solid rgba(255,107,26,.4);color:var(--oil);cursor:pointer;letter-spacing:1px;}
.sim-btn:disabled{opacity:.4;cursor:not-allowed;}
.live-badge{font-size:8px;padding:3px 8px;border-radius:2px;background:rgba(255,68,102,.1);border:1px solid rgba(255,68,102,.3);color:var(--red);letter-spacing:2px;animation:blink 3s step-end infinite;}
@keyframes blink{0%,90%,100%{opacity:1}95%{opacity:.4}}

/* PIPELINE */
.pipeline{display:flex;gap:1px;padding:4px 14px;background:var(--bg2);border-bottom:1px solid var(--border);flex-shrink:0;}
.pipe-step{flex:1;border:1px solid var(--border);border-radius:2px;padding:4px 7px;background:var(--bg3);transition:all .2s;}
.pipe-step.active{border-color:var(--cyan);background:rgba(0,255,204,.06);box-shadow:0 0 10px rgba(0,255,204,.1);}
.pipe-step.done{border-color:rgba(0,255,136,.3);background:rgba(0,255,136,.04);}
.pipe-num{font-size:6px;color:var(--dim);letter-spacing:1px;margin-bottom:1px;}
.pipe-name{font-size:9px;font-weight:600;font-family:var(--sans);}
.pipe-step.active .pipe-name{color:var(--cyan);}
.pipe-step.done .pipe-name{color:var(--green);}
.pipe-step:not(.active):not(.done) .pipe-name{color:var(--dim);}
.pipe-time{font-size:7px;color:var(--dim);margin-top:1px;}
.pipe-step.active .pipe-time{color:var(--cyan);}
.pipe-step.done .pipe-time{color:var(--green);}

/* MAIN GRID */
.main{display:grid;grid-template-columns:260px 1fr 240px;grid-template-rows:1fr 200px;flex:1;gap:1px;background:var(--border);min-height:0;}
.panel{background:var(--bg);overflow:hidden;display:flex;flex-direction:column;}
.ph{font-size:9px;color:var(--dim);letter-spacing:2px;padding:6px 10px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;background:var(--bg2);flex-shrink:0;}
.ph .hi{color:var(--amber);}
.pb{flex:1;overflow-y:auto;padding:7px;}
.pb::-webkit-scrollbar{width:3px;} .pb::-webkit-scrollbar-thumb{background:var(--bright);}

/* LEFT PANEL */
.dossier{padding:10px 12px;border-bottom:1px solid var(--border);background:linear-gradient(135deg,rgba(0,255,136,.04),transparent);}
.dossier-tag{font-size:7px;color:var(--dim);letter-spacing:2px;margin-bottom:4px;display:flex;gap:8px;}
.dossier-tag span{background:rgba(0,255,136,.1);border:1px solid rgba(0,255,136,.2);color:var(--green);padding:1px 5px;border-radius:2px;}
.handle{font-size:16px;font-weight:700;font-family:var(--sans);color:var(--text);margin-bottom:6px;}
.pnl-row{display:flex;gap:16px;margin-bottom:3px;}
.pnl-block{display:flex;flex-direction:column;}
.pnl-label{font-size:7px;color:var(--dim);letter-spacing:1px;}
.pnl-val{font-size:18px;font-weight:700;font-family:var(--sans);}
.stats-row{display:flex;gap:12px;margin-top:6px;}
.stat-block{display:flex;flex-direction:column;}
.stat-label{font-size:7px;color:var(--dim);letter-spacing:1px;}
.stat-val{font-size:13px;font-weight:700;font-family:var(--sans);}

.strategy-box{padding:10px 12px;border-bottom:1px solid var(--border);font-size:9px;color:var(--dim);line-height:1.6;}
.strategy-box strong{color:var(--text);}
.strategy-box .hi{color:var(--gold);}

.signal-item{border:1px solid var(--border);border-radius:3px;margin-bottom:4px;padding:6px 8px;transition:all .15s;}
.signal-item:hover{border-color:var(--bright);background:var(--bg2);}
.sig-time{font-size:7px;color:var(--dim);margin-bottom:2px;}
.sig-head{font-size:9px;color:var(--text);line-height:1.4;margin-bottom:3px;}
.sig-cat{font-size:7px;padding:1px 4px;border-radius:2px;letter-spacing:1px;}

/* CENTER */
.graph-header{padding:6px 10px;border-bottom:1px solid var(--border);background:var(--bg2);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.gh-title{font-size:10px;color:var(--cyan);letter-spacing:2px;display:flex;align-items:center;gap:8px;}
.gh-scanning{font-size:8px;padding:2px 7px;background:rgba(0,255,204,.1);border:1px solid rgba(0,255,204,.3);color:var(--cyan);border-radius:2px;animation:blink 1s step-end infinite;}
.gh-meta{font-size:8px;color:var(--dim);display:flex;gap:12px;}
.gh-meta span{color:var(--text);}

.graph-wrap{flex:1;min-height:0;position:relative;}

.top-trade-panel{border-top:1px solid var(--border);padding:8px 12px;background:var(--bg2);flex-shrink:0;}
.tt-label{font-size:8px;color:var(--dim);letter-spacing:2px;margin-bottom:4px;}
.tt-signal{font-size:13px;font-weight:700;font-family:var(--sans);margin-bottom:3px;}
.tt-text{font-size:9px;color:var(--mid);line-height:1.5;}

/* RIGHT PANEL */
.mkt-card{border:1px solid var(--border);border-radius:3px;margin-bottom:5px;padding:7px 8px;background:var(--bg2);}
.mkt-q{font-size:9px;font-weight:600;font-family:var(--sans);color:var(--text);margin-bottom:4px;line-height:1.3;}
.mkt-prices{display:flex;justify-content:space-between;font-size:9px;margin-bottom:3px;}
.yes-p{color:var(--green);} .no-p{color:var(--red);}
.mkt-bar{height:3px;background:var(--bg3);border-radius:2px;overflow:hidden;margin-bottom:3px;}
.mkt-fill{height:100%;border-radius:2px;background:linear-gradient(90deg,var(--green),rgba(0,255,136,.3));}
.mkt-meta{font-size:7px;color:var(--dim);display:flex;justify-content:space-between;}

.mispricing-box{border:1px solid rgba(255,68,102,.3);border-radius:3px;background:rgba(255,68,102,.06);padding:8px 10px;margin-bottom:8px;}
.mb-label{font-size:8px;color:var(--dim);letter-spacing:2px;margin-bottom:5px;}
.mb-row{display:flex;justify-content:space-between;font-size:9px;margin-bottom:3px;}
.mb-l{color:var(--dim);} .mb-v{color:var(--text);}
.short-btn{width:100%;margin-top:8px;font-family:var(--mono);font-size:9px;padding:6px;border-radius:2px;background:rgba(255,68,102,.15);border:1px solid rgba(255,68,102,.5);color:var(--red);cursor:pointer;letter-spacing:2px;font-weight:700;}
.short-btn:hover{background:rgba(255,68,102,.25);}

/* BOTTOM: LOG */
.bottom-panel{grid-column:1/-1;grid-row:2;display:flex !important;overflow:hidden;flex-direction:row !important;}
.pnl-section{width:200px;border-right:1px solid var(--border);display:flex;flex-direction:column;flex-shrink:0;}
.pnl-curve-wrap{flex:1;padding:6px 8px;overflow:hidden;min-height:0;display:flex;flex-direction:column;}
.edge-section{display:none;}
.edge-bars{flex:1;padding:4px 6px;display:flex;align-items:flex-end;gap:2px;min-height:0;}
.edge-bar-item{flex:1;background:var(--amber);border-radius:1px 1px 0 0;opacity:.7;transition:height .3s;}
.trade-log{flex:1;overflow:hidden;display:flex;flex-direction:column;min-width:0;}
.log-hdr{display:grid;grid-template-columns:65px 48px 1fr 50px 50px 52px 65px;gap:6px;padding:4px 8px;border-bottom:1px solid var(--bright);font-size:8px;color:var(--dim);letter-spacing:1px;}
.log-row{display:grid;grid-template-columns:65px 48px 1fr 50px 50px 52px 65px;gap:6px;align-items:center;padding:4px 8px;border-bottom:1px solid rgba(255,255,255,.02);font-size:9px;}
.log-row:hover{background:rgba(255,255,255,.015);}
.lr-time{color:var(--dim);font-size:8px;}
.lr-dir.no{color:var(--red);font-weight:700;} .lr-dir.yes{color:var(--green);font-weight:700;}
.lr-mkt{color:var(--mid);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.lr-size{text-align:right;} .lr-price{color:var(--amber);text-align:right;} .lr-edge{color:var(--cyan);text-align:right;}
.lr-pnl{text-align:right;font-weight:700;}
.lr-pnl.pos{color:var(--green);} .lr-pnl.neg{color:var(--red);} .lr-pnl.open{color:var(--dim);}

/* BOTTOM STATUS BAR */
.status-bar{display:flex;gap:0;padding:0 12px;background:var(--bg2);border-top:1px solid var(--bright);flex-shrink:0;height:22px;align-items:center;}
.sb-item{font-size:8px;padding:0 10px;border-right:1px solid var(--border);display:flex;align-items:center;gap:5px;}
.sb-item:last-child{border:none;}
.sb-label{color:var(--dim);letter-spacing:1px;}
.sb-val{color:var(--text);}
.sb-val.green{color:var(--green);} .sb-val.amber{color:var(--amber);} .sb-val.cyan{color:var(--cyan);}
`;

  const topMarket = simResult?.markets?.[0] || markets[0];
  const topDir    = simResult?.top_trade?.direction || "NO";

  // ── COINMAN ARCHITECTURE (coinman2 style — see x.com/antpalkin/2046654122892403188) ──
  // 1. Coinbase WS streams BTC price in real-time (browser, no CF Worker IP block)
  // 2. Local price history → change30s/1m/5m → momentum (sub-second latency)
  // 3. Worker poll every 30s → fetches markets + kronos only
  // 4. Edges recompute every WS tick using current markets + live momentum
  // 5. Auto-fire when edge >= 3pt and not in 2min cooldown

  // Worker poll: markets + kronos (no price/momentum, WS handles those)
  const lastMarketFetchRef = useRef(0); // BUG VVVV: track last successful market fetch
  const fetchPolyBtcMarkets = useCallback(async () => {
    try {
      const res = await fetch(`${WORKER}/api/btc/scan`);
      if (!res.ok) throw new Error("Worker returned " + res.status);
      const d = await res.json();

      if (d.kronos) setCoinmanKronos(d.kronos);

      if (Array.isArray(d.markets) && d.markets.length) {
        setPolyBtcMkts(d.markets);
        lastMarketFetchRef.current = Date.now(); // BUG VVVV: success timestamp
      }

      setBtcMomentum(prev => ({
        ...prev,
        change15m: parseFloat((d.btcChange15m || prev.change15m || 0).toFixed(3)),
        change1h:  parseFloat((d.btcChange1h  || prev.change1h  || 0).toFixed(3)),
      }));

      if (d.btcPrice && d.btcPrice > 1000) {
        setBinancePrice(prev => prev || d.btcPrice);
      }
    } catch(e) {
      console.error("fetchPolyBtcMarkets error:", e.message);
      // BUG VVVV: if last successful fetch > 90s ago, warn loudly
      const stale = Date.now() - lastMarketFetchRef.current;
      if (lastMarketFetchRef.current > 0 && stale > 90000) {
        console.warn(`[COINMAN] markets stale ${(stale/1000).toFixed(0)}s — worker likely down. Bot will fire on stale data.`);
      }
    }
  }, []);

  useEffect(() => {
    if (activeTab !== "coinman") return;
    fetchPolyBtcMarkets();
    const t = setInterval(fetchPolyBtcMarkets, 30000);
    return () => clearInterval(t);
  }, [activeTab, fetchPolyBtcMarkets]);

  // ── COINBASE WEBSOCKET — live BTC price, real-time momentum, real-time edges ──
  useEffect(() => {
    if (activeTab !== "coinman") return;

    let ws = null;
    let priceHistory = [];   // {price, ts}[]
    let lastStateUpdate = 0;
    let lastMessageTime = Date.now();   // BUG D: heartbeat — track last message timestamp
    let reconnectTimer = null;
    let watchdogTimer = null;
    let mounted = true;                  // BUG C: prevents ghost reconnect after unmount

    const compute = (price, now) => {
      const calc = (msAgo) => {
        const cutoff = now - msAgo;
        const past = priceHistory.find(p => p.ts >= cutoff);
        if (!past) return 0;
        return ((price - past.price) / past.price) * 100;
      };
      return {
        change30s: parseFloat(calc(30000).toFixed(3)),
        change1m:  parseFloat(calc(60000).toFixed(3)),
        change5m:  parseFloat(calc(300000).toFixed(3)),
      };
    };

    const recomputeEdges = (price, ch) => {
      // ── T-10s MOMENTUM FOLLOW STRATEGY ────────────────────────────────────
      // At T-15s before market close, follow whichever direction is winning.
      // At T-15s, BTC rarely reverses enough to flip outcome — direction is locked.
      // Per Archetapp research: 55-60% win rate vs 50% random on this approach.
      //
      // We snapshot BTC price at market FIRST observation (proxy for open price),
      // then at T-15s, compare current BTC to that open.
      //   - BTC went up → buy YES (Up side) regardless of polyP
      //   - BTC went down → buy NO (Down side) regardless of polyP
      // Token may cost 0.55-0.85 but high accuracy compensates.
      const nowMs = Date.now();

      setPolyBtcMkts(prev => {
        if (!prev || !prev.length) return prev;

        const edges = prev.map(mkt => {
          const slug = mkt.slug || mkt._slug || "";
          const slugExpiryMs = parseSlugExpiryMs(slug);
          const slugStartMs = parseSlugStartMs(slug);

          // Only trade markets we can parse expiry from
          if (!slugExpiryMs || !slugStartMs) {
            return { ...mkt, polyP: mkt.yesPrice||0.5, edgePct: 0, dir: "SKIP", signal: "WAIT", btcPrice: price, change5m: ch, target: "no slug" };
          }

          const side = parseMarketSide(mkt.question);
          if (side === "UNKNOWN") {
            return { ...mkt, polyP: mkt.yesPrice||0.5, edgePct: 0, dir: "SKIP", signal: "WAIT", btcPrice: price, change5m: ch, target: "side unknown" };
          }

          // Bug ØØØØ: skip if no market id (cache would key on undefined)
          if (!mkt.id) {
            return { ...mkt, polyP: mkt.yesPrice||0.5, edgePct: 0, dir: "SKIP", signal: "WAIT", btcPrice: price, change5m: ch, target: "no id" };
          }

          // BUG ÆÆÆÆ FIX: use chart history to find ACTUAL BTC price at window start
          // (not first-observation proxy — that's unreliable since worker may scan after window started)
          // Cache the result so we don't re-search history every cycle
          if (!openPriceCacheRef.current) openPriceCacheRef.current = {};

          // BUG ¶¶¶¶ FIX: GC stale cache entries (markets that already expired) to prevent memory leak
          // Only run GC ~1% of the time to avoid hot-path overhead
          if (Math.random() < 0.01) {
            const cutoff = nowMs - 30*60*1000; // 30min ago
            for (const k of Object.keys(openPriceCacheRef.current)) {
              const entry = openPriceCacheRef.current[k];
              if (entry?.slugStartMs && entry.slugStartMs < cutoff) {
                delete openPriceCacheRef.current[k];
              }
            }
          }

          if (!openPriceCacheRef.current[mkt.id]) {
            // Search chart history for the price closest to slugStartMs (within 30s)
            const hist = btcChartHistoryRef.current || [];
            let bestSample = null;
            let bestDelta = Infinity;
            for (const sample of hist) {
              const delta = Math.abs(sample.ts - slugStartMs);
              if (delta < bestDelta && delta < 30000) {
                bestDelta = delta;
                bestSample = sample;
              }
            }
            if (bestSample && bestSample.price > 0) {
              openPriceCacheRef.current[mkt.id] = {
                openPrice: bestSample.price,
                slugStartMs,
                source: "history",
                histDeltaMs: bestDelta,
              };
            } else if (price > 0 && (nowMs - slugStartMs) < 60000) {
              // Fallback: only use current price as proxy IF we're within first 60s of window
              // (otherwise the proxy is too stale to be useful)
              openPriceCacheRef.current[mkt.id] = {
                openPrice: price,
                slugStartMs,
                source: "proxy",
                proxyAgeMs: nowMs - slugStartMs,
              };
            }
          }

          const cache = openPriceCacheRef.current[mkt.id];
          const secsToExpiry = (slugExpiryMs - nowMs) / 1000;
          const secsFromStart = (nowMs - slugStartMs) / 1000;

          // Fire window: between T-25s and T-12s before expiry
          // (gives time to actually fill before the rush)
          const inFireWindow = secsToExpiry > 12 && secsToExpiry <= 25;

          if (!inFireWindow) {
            const reason = secsToExpiry <= 12 ? "TOO_LATE" :
                          secsToExpiry > 25 ? "EARLY" : "SKIP";
            return { ...mkt, polyP: mkt.yesPrice||0.5, edgePct: 0, dir: reason, signal: "WAIT", btcPrice: price, change5m: ch, target: `T-${secsToExpiry.toFixed(0)}s` };
          }

          // Need open price to determine direction
          if (!cache || cache.openPrice <= 0) {
            return { ...mkt, polyP: mkt.yesPrice||0.5, edgePct: 0, dir: "NO_OPEN", signal: "WAIT", btcPrice: price, change5m: ch, target: "no open snapshot" };
          }

          // CORE LOGIC: where is BTC vs window open?
          const btcDelta = price - cache.openPrice;
          const btcDeltaPct = (btcDelta / cache.openPrice) * 100;

          const polyYes = mkt.yesPrice || 0.5;
          const polyNo  = mkt.noPrice  || (1 - polyYes);

          let dir, polyP, target;

          if (btcDelta > 0) {
            // BTC is up vs open — for UP-style markets, YES is winning; for DOWN markets, NO is winning
            if (side === "UP") {
              dir = "YES"; polyP = polyYes;
            } else {
              dir = "NO"; polyP = polyNo;
            }
            target = `BTC +${btcDeltaPct.toFixed(3)}% at T-${secsToExpiry.toFixed(0)}s`;
          } else if (btcDelta < 0) {
            // BTC is down vs open
            if (side === "UP") {
              dir = "NO"; polyP = polyNo;
            } else {
              dir = "YES"; polyP = polyYes;
            }
            target = `BTC ${btcDeltaPct.toFixed(3)}% at T-${secsToExpiry.toFixed(0)}s`;
          } else {
            // Exactly flat — no signal
            return { ...mkt, polyP: polyYes, edgePct: 0, dir: "FLAT", signal: "WAIT", btcPrice: price, change5m: ch, target: "btc flat" };
          }

          // edgePct represents move magnitude (proxy for confidence)
          // 0.05% move = solid signal, 0.2%+ = very strong
          const edgePct = Math.abs(btcDeltaPct) * 100; // 0.05% → 5

          // Don't fire if token too expensive (we'd need to be 95%+ confident)
          if (polyP >= 0.95) {
            return { ...mkt, polyP, edgePct: 0, dir: "TOO_PRICEY", signal: "WAIT", btcPrice: price, change5m: ch, target: `${dir} @ ${(polyP*100).toFixed(0)}¢` };
          }

          return {
            ...mkt,
            impliedP: parseFloat(polyYes.toFixed(3)),
            polyP:    parseFloat((polyP || 0.5).toFixed(3)),
            edgePct:  parseFloat(edgePct.toFixed(2)),
            dir,
            side,
            target,
            openPrice: cache.openPrice,
            btcDelta: parseFloat(btcDelta.toFixed(2)),
            btcDeltaPct: parseFloat(btcDeltaPct.toFixed(4)),
            signal:   edgePct >= 1 ? "BUY" : "WAIT", // require BTC moved at least 0.01% from open
            btcPrice: price,
            change5m: ch,
          };
        }).filter(e => e.edgePct >= 1).sort((a,b) => b.edgePct - a.edgePct);

        setCoinmanEdges(edges);
        return prev;
      });
    };

    const connect = () => {
      // BUG MM FIX: clear any pending reconnect so we don't double-connect
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
      try {
        ws = new WebSocket("wss://ws-feed.exchange.coinbase.com");
        binanceWsRef.current = ws;

        ws.onopen = () => {
          console.log("[COINMAN WS] coinbase connected");
          ws.send(JSON.stringify({
            type: "subscribe",
            product_ids: ["BTC-USD"],
            channels: ["ticker"]
          }));
        };

        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            // BUG UUUU FIX: surface coinbase error responses
            if (msg.type === "error") {
              console.error("[COINMAN WS] coinbase error:", msg.message || msg);
              return;
            }
            if (msg.type === "ticker" && msg.price) {
              const price = parseFloat(msg.price);
              const now = Date.now();
              lastMessageTime = now; // BUG D: heartbeat
              priceHistory.push({ price, ts: now });
              priceHistory = priceHistory.filter(p => now - p.ts < 600000); // 10min window

              // Throttle state updates to 1Hz max — coinbase fires 5-50/s
              if (now - lastStateUpdate < 1000) return;
              lastStateUpdate = now;

              const { change30s, change1m, change5m } = compute(price, now);
              const dir =
                Math.abs(change30s) > 0.02 ? (change30s > 0 ? "UP" : "DOWN") :
                Math.abs(change1m)  > 0.04 ? (change1m  > 0 ? "UP" : "DOWN") :
                Math.abs(change5m)  > 0.05 ? (change5m  > 0 ? "UP" : "DOWN") :
                "NEUTRAL";

              setBinancePrice(price);
              setBtcMomentum(prev => ({
                ...prev,
                price,
                change30s,
                change1m,
                change5m,
                direction: dir,
                samples: priceHistory.length,
              }));

              // Push to chart history (last 1200 points = ~20min @ 1Hz, covers 15m market open lookups)
              setBtcChartHistory(prev => {
                const next = [...prev, { ts: now, price }];
                return next.slice(-1200);
              });

              // Recompute edges + auto-fire using whichever change has signal
              const liveCh = change30s !== 0 ? change30s : (change1m !== 0 ? change1m : change5m);
              recomputeEdges(price, liveCh);
            }
          } catch(e) {}
        };

        ws.onerror = () => { console.log("[COINMAN WS] error"); };
        ws.onclose = () => {
          console.log("[COINMAN WS] closed");
          // BUG C FIX: only reconnect if effect is still mounted
          if (mounted) {
            console.log("[COINMAN WS] scheduling reconnect in 5s");
            reconnectTimer = setTimeout(connect, 5000);
          }
        };
      } catch(e) {
        console.log("[COINMAN WS] failed:", e.message);
      }
    };

    connect();

    // BUG D FIX: heartbeat watchdog — if no message in 60s, force reconnect
    watchdogTimer = setInterval(() => {
      if (!mounted) return;
      const since = Date.now() - lastMessageTime;
      if (since > 60000 && ws && ws.readyState === WebSocket.OPEN) {
        console.warn(`[COINMAN WS] no messages for ${(since/1000).toFixed(0)}s, force reconnecting`);
        try { ws.close(); } catch(e) {} // triggers onclose → reconnect (since mounted=true)
      } else if (since > 90000 && ws && ws.readyState !== WebSocket.OPEN) {
        // Connection stuck closing/connecting — bail out, force new connect
        console.warn("[COINMAN WS] connection stuck, force connect");
        try { ws.close(); } catch(e) {}
        ws = null;
        if (mounted) {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 1000);
        }
      }
    }, 30000);

    return () => {
      mounted = false; // BUG C FIX: set first so onclose won't schedule new reconnect
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (watchdogTimer) clearInterval(watchdogTimer);
      if (ws) { try { ws.close(); } catch(e) {} }
      binanceWsRef.current = null;
    };
  }, [activeTab]);

  const fireCoinmanTrade = useCallback((edge) => {
    const br = Number.isFinite(bankrollRef.current) ? bankrollRef.current : (Number.isFinite(bankroll) ? bankroll : 1000);
    const kb = Math.max(10, Math.min(br * 0.032, 200));
    const now = Date.now();
    const entryPrice = currentPriceRef.current || binancePrice || 0;

    // BUG K FIX: validate edge
    if (!edge || !edge.id || !edge.question || !edge.dir || edge.polyP == null) {
      console.warn("[COINMAN FIRE] invalid edge data, refusing fire", edge);
      return;
    }
    if (edge.polyP < 0.10 || edge.polyP > 0.90 || !Number.isFinite(edge.polyP)) {
      console.warn("[COINMAN FIRE] extreme polyP, refusing fire (data sanity):", edge.polyP);
      return;
    }
    if (br < kb + 10) {
      console.warn("[COINMAN FIRE] bankroll too low to fire safely");
      return;
    }
    if (entryPrice <= 0) {
      console.warn("[COINMAN FIRE] no BTC price yet, skipping fire");
      return;
    }

    // BUG AAAAA FIX: max-pending guard — same cap as cycle (10) prevents runaway exposure
    if ((pendingTradesRef.current || []).length >= 10) {
      console.warn("[COINMAN FIRE] 10 pending open, refusing fire (max concurrent)");
      return;
    }

    // T-15s strategy: each market fires once per window
    const lastFired = lastFiredMarketsRef.current[edge.id] || 0;
    if (now - lastFired < 60000) {
      console.warn(`[COINMAN FIRE] market ${edge.id.slice(0,10)} fired ${((now-lastFired)/1000).toFixed(0)}s ago, cooldown`);
      return;
    }

    const slug = edge.slug || edge._slug || edge.question || "";
    const slugExpiryMs = parseSlugExpiryMs(slug);
    const expiryMs = slugExpiryMs || (now + 5*60*1000);
    const durationMs = expiryMs - now;
    if (durationMs < 10000) {
      console.warn("[COINMAN FIRE] market expires in <10s, skipping");
      return;
    }

    // BUG CCCCC FIX: update cooldown ref so cycle won't fire same market right after
    lastFiredMarketsRef.current[edge.id] = now;

    const effectivePolyP = applySlippage(edge.polyP);

    const tradeId = `${edge.id}_${now}_${Math.random().toString(36).slice(2,7)}`;

    // BUG ●●●●● FIX: capture window open price for correct W/L resolution
    const cacheForOpenManual = openPriceCacheRef.current?.[edge.id];
    const windowOpenPriceManual = cacheForOpenManual?.openPrice || entryPrice;

    pendingTradesRef.current.push({
      tradeId,
      marketId: edge.id, // EARLY-EXIT: needed to poll live polyP
      market: edge.question || "",
      dir: edge.dir,
      side: edge.side || parseMarketSide(edge.question),
      kb,
      polyP: effectivePolyP,
      rawPolyP: edge.polyP || 0.5,
      entryPrice,
      windowOpenPrice: windowOpenPriceManual,
      expiryMs,
      durationMs,
      firedAt: now,
    });
    saveCoinmanPending(pendingTradesRef.current);

    setCoinmanLog(prev => {
      const next = [{
        tradeId,
        time:       new Date().toLocaleTimeString("en-US",{hour12:false}),
        market:     (edge.question||"").slice(0,45),
        dir:        edge.dir,
        edge:       (edge.edgePct||0).toFixed(1)+"pt",
        size:       "$"+kb.toFixed(0),
        btc:        "$"+entryPrice.toLocaleString(),
        change:     (edge.change5m||0).toFixed(3)+"% live",
        pnl:        0,
        status:     "PENDING",
        entryPrice,
        expiryMs,
        durationMs,
        kb,
        polyP:      effectivePolyP,
        isDownMarket: parseMarketSide(edge.question) === "DOWN",
      }, ...prev].slice(0,500);
      saveCoinmanLog(next); // BUG ZZ FIX: save immediately
      return next;
    });
    setBankroll(b => { const nv=parseFloat((b-kb).toFixed(2)); saveBankroll(nv); return nv; });
  }, [bankroll, binancePrice]);

  // ── EARLY RETURN FOR COINMAN TAB ─────────────────────────────
  if (activeTab === "coinman") {
    return (
      <div style={{display:"flex",flexDirection:"column",height:"100vh",background:"#080f14",overflow:"hidden"}}>
        <div style={{display:"flex",background:"#050c12",borderBottom:"2px solid rgba(0,255,136,.15)",padding:"0 16px",flexShrink:0}}>
          {[{id:"mirofish",label:"🌊  MIROFISH ENGINE"},{id:"coinman",label:"⚡  BTC COINMAN"}].map(tab=>(
            <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{fontFamily:"monospace",fontSize:9,letterSpacing:2,padding:"8px 20px",cursor:"pointer",background:"transparent",border:"none",borderBottom:activeTab===tab.id?"2px solid #00ff88":"2px solid transparent",color:activeTab===tab.id?"#00ff88":"rgba(100,100,100,.6)",marginBottom:"-2px"}}>{tab.label}</button>
          ))}
        </div>
        <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
          <CoinmanTab
            btcPrice={binancePrice}
            btcChange={btcMomentum.change30s||0}
            change1m={btcMomentum.change1m||0}
            change5m={btcMomentum.change5m||0}
            change15m={btcMomentum.change15m||0}
            change1h={btcMomentum.change1h||0}
            samples={btcMomentum.samples||0}
            wsConnected={!!binanceWsRef.current}
            kronos={coinmanKronos}
            markets={polyBtcMkts} edges={coinmanEdges} log={coinmanLog} autoOn={coinmanAuto}
            cycleNumber={coinmanCycle.n}
            cycleStartMs={coinmanCycle.startMs}
            sessionStartMs={sessionStartRef.current}
            chartHistory={btcChartHistory}
            onToggleAuto={()=>{const n=!coinmanAuto;setCoinmanAuto(n);coinmanAutoRef.current=n;}}
            onFire={fireCoinmanTrade} onRefresh={fetchPolyBtcMarkets} bankroll={bankroll}
            onReset={(mode) => {
              // Clear pending + log refs and state
              pendingTradesRef.current = [];
              saveCoinmanPending([]);
              setCoinmanLog([]);
              saveCoinmanLog([]);
              setBtcChartHistory([]);
              saveCoinmanPriceHist([]);
              openPriceCacheRef.current = {}; // Clear T-15s window-open snapshots
              if (mode === "full") {
                setBankroll(1000); saveBankroll(1000);
                setSessionPnl(0); saveSessionPnl(0);
                bankrollRef.current = 1000;
              }
              console.log(`[COINMAN] reset mode=${mode}`);
            }}/>
        </div>
      </div>
    );
  }


  return (
    <>
      <style>{CSS}</style>
      <div className="root">
        {/* TAB SWITCHER */}
        <div style={{display:"flex",background:"#0a1628",borderBottom:"2px solid #00ff88",padding:"0 16px",flexShrink:0,zIndex:200,width:"100%",minHeight:"34px",alignItems:"center"}}>
          {[{id:"mirofish",label:"🌊  MIROFISH ENGINE"},{id:"coinman",label:"⚡  BTC COINMAN"}].map(tab=>(
            <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{fontFamily:"monospace",fontSize:10,letterSpacing:2,padding:"6px 20px",cursor:"pointer",background:activeTab===tab.id?"rgba(0,255,136,.1)":"transparent",border:"none",borderBottom:activeTab===tab.id?"3px solid #00ff88":"3px solid transparent",color:activeTab===tab.id?"#00ff88":"#888",fontWeight:activeTab===tab.id?"bold":"normal"}}>{tab.label}</button>
          ))}
        </div>
        <div className="scanlines"/>

        {/* LIVE FILLS TICKER */}
        <div className="ticker-wrap">
          <div className="ticker-inner">
            {[...liveFills,...liveFills.slice(0,5),...MOCK_SIGNALS.map(s=>({ time:s.time, dir:"NO", market:s.headline.slice(0,30)+"...", pnl:(Math.random()*200-50).toFixed(2), edge:"+15¢", conf:"72%" }))].map((f,i)=>(
              <div key={i} className="ti">
                <span className="ti-label">● {f.dir||"NO"}</span>
                <span className="ti-val">{f.market}</span>
                <span className={`ti-pnl ${parseFloat(f.pnl)>=0?"pos":"neg"}`}>{parseFloat(f.pnl)>=0?"+$":"$"}{Math.abs(parseFloat(f.pnl)).toFixed(2)}</span>
                <span className="ti-conf">CONF {f.conf}</span>
                <span className="ti-edge">EDGE {f.edge}</span>
              </div>
            ))}
          </div>
        </div>

        {/* HEADER */}
        <div className="hdr">
          <div className="logo-block">
            <div className="logo-title">
              <div className="logo-dot"/>
              MiroFish Simulation Engine
            </div>
            <div className="logo-sub">WTI DAILY · EDGE DETECTION · {autoRunning?"AUTO":"MANUAL"}</div>
          </div>
          <div className="hdr-stats">
            <div className="hstat"><div className="hstat-l">WALLET</div><div className="hstat-v">${bankroll.toFixed(2)}</div></div>
            <div className="hstat"><div className="hstat-l">ALL-TIME PnL</div><div className={`hstat-v ${allPnl>=0?"green":"red"}`}>{allPnl>=0?"+$":"-$"}{Math.abs(allPnl).toFixed(2)}</div></div>
            <div className="hstat"><div className="hstat-l">BIGGEST WIN</div><div className="hstat-v amber">${bigWin.toFixed(2)}</div></div>
            <div className="hstat"><div className="hstat-l">TRADES</div><div className="hstat-v">{trades.length}</div></div>
            <div className="hstat"><div className="hstat-l">WIN RATE</div><div className="hstat-v green">{winRate}%</div></div>
            <div className="hstat"><div className="hstat-l">CYCLE</div><div className="hstat-v cyan">#{cycleCount}</div></div>
          </div>
          <div className="hdr-right">
          <button onClick={()=>{ if(window.confirm("Reset wallet to $1000?")){ saveBankroll(1000); setBankroll(1000); saveSessionPnl(0); setSessionPnl(0); }}} style={{fontFamily:"var(--mono)",fontSize:8,padding:"3px 8px",borderRadius:2,background:"rgba(74,112,144,.08)",border:"1px solid rgba(74,112,144,.2)",color:"var(--dim)",cursor:"pointer"}}>
            RESET
          </button>
            <button onClick={()=>{setArbTab(!arbTab); if(!arbTab&&arbOpps.length===0) scanArb();}} style={{fontFamily:"var(--mono)",fontSize:9,padding:"5px 12px",borderRadius:2,background:arbTab?"rgba(168,85,247,.2)":"rgba(168,85,247,.08)",border:arbTab?"1px solid rgba(168,85,247,.6)":"1px solid rgba(168,85,247,.3)",color:"var(--purple)",cursor:"pointer",letterSpacing:1}}>
              {arbScanning?"⟳ SCANNING...":"⚡ NEGRISK ARB"}
            </button>
            <button onClick={()=>{
              if(window.confirm("Clear all trades and reset bankroll to $1000?")) {
                localStorage.removeItem("mirofish_trades");
                localStorage.removeItem("mirofish_bankroll");
                localStorage.removeItem("mirofish_session_pnl");
                localStorage.removeItem("mf_last_trade_time"); localStorage.removeItem("mf_last_trade_mkt");
                window.location.reload();
              }
            }} style={{fontFamily:"var(--mono)",fontSize:8,padding:"4px 10px",borderRadius:2,background:"rgba(255,68,102,.1)",border:"1px solid rgba(255,68,102,.3)",color:"var(--red)",cursor:"pointer",letterSpacing:1}}>
              ⟳ RESET
            </button>
            {cooldownSec > 0 && <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--amber)",padding:"3px 8px",border:"1px solid rgba(245,158,11,.3)",borderRadius:2}}>
              ⏳ NEXT TRADE: {cooldownSec}s
            </span>}
            <button className="sim-btn" onClick={()=>runPipeline(false)} disabled={simulating||autoRunning}>
              {simulating?"SCANNING...":"⚡ RUN CYCLE"}
            </button>
            <span style={{fontFamily:"var(--mono)",fontSize:8,padding:"3px 8px",borderRadius:2,
          background:wsStatus==="live"?"rgba(0,255,136,.15)":wsStatus==="connecting"?"rgba(245,158,11,.15)":"rgba(255,68,102,.1)",
          border:`1px solid ${wsStatus==="live"?"rgba(0,255,136,.4)":wsStatus==="connecting"?"rgba(245,158,11,.4)":"rgba(255,68,102,.3)"}`,
          color:wsStatus==="live"?"var(--green)":wsStatus==="connecting"?"var(--amber)":"var(--red)",
          letterSpacing:1}}>
          {wsStatus==="live"?"⚡ WS LIVE":wsStatus==="connecting"?"⟳ WS CONNECTING...":"○ WS OFF"}
        </span>
        <button className={`auto-btn ${autoRunning?"on":"off"}`} onClick={toggleAuto}>
              {autoRunning?"■ STOP AUTO":"▶ AUTO TRADE"}
            </button>
            <div className="live-badge">● {autoRunning?"AUTO LIVE":"LIVE"}</div>
          </div>
        </div>

        {/* PIPELINE */}
        {/* CATEGORY TOGGLE BAR */}
        <div style={{display:"flex",alignItems:"center",gap:5,padding:"4px 16px",background:"rgba(0,0,0,.5)",borderBottom:"1px solid rgba(100,100,100,.25)",flexWrap:"wrap",position:"relative"}}>
          <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)",letterSpacing:1,marginRight:2}}>TRADE:</span>
          {[{id:"oil",label:"⛽ OIL",color:"#f59e0b"},{id:"crypto",label:"₿ CRYPTO",color:"#a78bfa"},{id:"econ",label:"📊 ECON",color:"#60a5fa"},{id:"fed",label:"🏦 FED",color:"#34d399"},{id:"weather",label:"🌡 WEATHER",color:"#6ee7b7"},{id:"politics",label:"🗳 POLITICS",color:"#f87171"}].map(cat=>(
            <button key={cat.id} onClick={()=>setActiveCategories(prev=>prev.includes(cat.id)?prev.filter(x=>x!==cat.id):[...prev,cat.id])}
              style={{fontFamily:"var(--mono)",fontSize:7,padding:"2px 7px",borderRadius:10,cursor:"pointer",letterSpacing:1,background:activeCategories.includes(cat.id)?cat.color+"22":"transparent",border:"1px solid "+(activeCategories.includes(cat.id)?cat.color:"rgba(100,100,100,.3)"),color:activeCategories.includes(cat.id)?cat.color:"rgba(150,150,150,.6)"}}>
              {cat.label}{activeCategories.includes(cat.id)?" ✓":""}
            </button>
          ))}
          <button onClick={()=>setShowMarketPicker(p=>!p)} style={{fontFamily:"var(--mono)",fontSize:7,padding:"2px 7px",borderRadius:2,cursor:"pointer",background:"transparent",border:"1px solid rgba(100,100,100,.3)",color:"rgba(150,150,150,.6)",letterSpacing:1}}>📌 PIN</button>
          {cooldownSec>0&&<span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--amber)"}}>⏳ {cooldownSec}s</span>}
          {learning?.patterns&&<span style={{fontFamily:"var(--mono)",fontSize:7,color:"#a78bfa",marginLeft:4}}>🧠 {learning.patterns.bestCategory?.toUpperCase()||"LEARNING"}</span>}
        </div>
        <div className="pipeline">
          {PIPELINE_STEPS.map((step,i)=>(
            <div key={step} className={`pipe-step ${pipelineStep===i?"active":pipelineStep>i||pipelineTimes[i]>0?"done":""}`}>
              <div className="pipe-num">{"0"+(i+1)}</div>
              <div className="pipe-name">{step}</div>
              <div className="pipe-time">{pipelineTimes[i]>0?`${pipelineTimes[i]}ms`:pipelineStep===i?"...":"---"}</div>
            </div>
          ))}
        </div>

        {/* MAIN */}
        <div className="main">

          {/* LEFT */}
          <div className="panel">
            <div className="dossier">
              <div className="dossier-tag">
                <span>● VERIFIED</span>
                <span>POLYGON</span>
              </div>
              <div className="handle">@trademonkey25</div>
              <div className="pnl-row">
                <div className="pnl-block">
                  <div className="pnl-label">SESSION</div>
                  <div className="pnl-val" style={{color:sessionPnl>=0?"var(--green)":"var(--red)", fontSize:18}}>{sessionPnl>=0?"+$":"-$"}{Math.abs(sessionPnl).toFixed(2)}</div>
                </div>
                <div className="pnl-block">
                  <div className="pnl-label">ALL-TIME</div>
                  <div className="pnl-val" style={{color:allPnl>=0?"var(--green)":"var(--red)", fontSize:18}}>{allPnl>=0?"+$":"-$"}{Math.abs(allPnl).toFixed(2)}</div>
                </div>
              </div>
              <div className="stats-row">
                <div className="stat-block"><div className="stat-label">BEST TRADE</div><div className="stat-val" style={{color:"var(--amber)"}}>${bigWin.toFixed(2)}</div></div>
                <div className="stat-block"><div className="stat-label">WIN RATE</div><div className="stat-val" style={{color:"var(--green)"}}>{winRate}%</div></div>
                <div className="stat-block"><div className="stat-label">TRADES</div><div className="stat-val">{trades.length}</div></div>
              </div>
            </div>

            <div className="strategy-box">
              <strong>STRATEGY · MIROFISH OIL SCANNER</strong><br/>
              Trades ALL Polymarket markets — oil, crypto, weather, economics, politics. Runs <strong>MiroFish relationship-graph simulation</strong> against live order book. When MiroFish detects <span className="hi">predicted odds diverge from market price</span> — enters with edge. Hunts <span className="hi">5-40¢ gaps</span> via 6-cycle pipeline. Pure mispricing alpha. No leverage.
              {simResult && <div style={{marginTop:6,padding:"4px 6px",background:"rgba(255,107,26,.08)",borderLeft:"2px solid var(--oil)",fontSize:9,color:"var(--mid)"}}>{simResult.dominant_signal}</div>}
            </div>

            <div className="ph">LIVE MARKET OSINT <span className="hi">LIVE</span></div>
            <div className="pb">
              {signals.map(s=>(
                <div key={s.id} className="signal-item">
                  <div className="sig-time">{s.time} ET</div>
                  <div className="sig-head">{s.headline}</div>
                  <span className="sig-cat" style={{background:`${CAT_COLORS[s.category]}15`,color:CAT_COLORS[s.category],border:`1px solid ${CAT_COLORS[s.category]}30`}}>{s.category}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CENTER */}
          <div className="panel" style={{display:"flex",flexDirection:"column"}}>

            {/* GRAPH HEADER */}
            <div className="graph-header">
              <div className="gh-title">
                {simulating && <span className="gh-scanning">● SCANNING</span>}
                Relationship Graph Simulation · WTI T+24h
              </div>
              <div className="gh-meta">
                <span>NODES <span>{nodeCount}</span></span>
                <span>EDGES <span>{edgeCount}</span></span>
                <span>PATHS <span>2,048</span></span>
                <span>ITER <span>{cycleCount*55+300}</span></span>
              </div>
            </div>

            {/* GRAPH — shrinks when arb panel is open */}
            <div style={{flex: arbTab ? "0 0 50%" : "1", minHeight:0, position:"relative", transition:"flex .3s"}}>
              <GraphCanvas simResult={simResult} simulating={simulating} running={autoRunning} pipelineStep={pipelineStep}/>
              <div style={{position:"absolute",top:8,left:8,display:"flex",flexDirection:"column",gap:3,background:"rgba(6,12,16,.8)",padding:"6px 8px",borderRadius:3,border:"1px solid var(--border)"}}>
                {[["BEAR SIGNAL","#ff4466"],["BULL SIGNAL","#00ff88"],["MEDIAN PATH","#00ffcc"],["ANCHOR","#ff6b1a"],["CLUSTER HUB","#e8c840"]].map(([l,col])=>(
                  <div key={l} style={{display:"flex",alignItems:"center",gap:5,fontSize:7,color:col}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:col,boxShadow:`0 0 4px ${col}`}}/>{l}
                  </div>
                ))}
              </div>
              {!simResult && !simulating && !arbTab && (
                <div style={{position:"absolute",bottom:40,left:"50%",transform:"translateX(-50%)"}}>
                  <button className="sim-btn" onClick={()=>runPipeline(false)} style={{padding:"9px 22px",fontSize:10,letterSpacing:2}}>⚡ RUN FULL SIMULATION</button>
                </div>
              )}
            </div>

            {/* TOP TRADE — below graph */}
            {simResult?.top_trade && !arbTab && (
              <div className="top-trade-panel">
                <div className="tt-label">◈ TOP TRADE — MIROFISH CALL</div>
                <div className="tt-signal" style={{color:simResult.top_trade.direction==="NO"?"var(--red)":"var(--green)"}}>
                  {topDir==="NO"?"▼":"▲"} {simResult.top_trade.direction==="NO"?"SHORT":"LONG"} WTI — {simResult.top_trade.question}
                </div>
                <div className="tt-text">{displayText}</div>
              </div>
            )}


            {/* BTC SCANNER PANEL */}
            <div style={{borderTop:"1px solid rgba(245,158,11,.3)",background:"rgba(245,158,11,.02)",padding:"6px 12px",flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <div style={{width:6,height:6,borderRadius:"50%",background:"var(--amber)",boxShadow:"0 0 6px var(--amber)"}}/>
                  <span style={{fontFamily:"var(--mono)",fontSize:9,color:"var(--amber)",letterSpacing:2}}>⚡ BTC PREDICTION SCANNER</span>
                  {btcData && <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)"}}>· BTC ${btcData.btcPrice?.toLocaleString()} · {btcData.momentum}</span>}
                </div>
                <button onClick={scanBtc} disabled={btcScanning} style={{fontFamily:"var(--mono)",fontSize:8,padding:"3px 10px",borderRadius:2,background:"rgba(245,158,11,.12)",border:"1px solid rgba(245,158,11,.4)",color:"var(--amber)",cursor:"pointer",letterSpacing:1}}>
                  {btcScanning?"⟳ SCANNING...":"⚡ SCAN BTC"}
                </button>
              </div>
              {btcData && btcData.kronos?.source==="kronos" && (
                <div style={{marginBottom:4,padding:"3px 8px",background:btcData.kronos.direction==="UP"?"rgba(0,255,136,.08)":btcData.kronos.direction==="DOWN"?"rgba(255,68,102,.08)":"rgba(100,100,100,.08)",border:"1px solid rgba(168,85,247,.3)",borderRadius:2,display:"flex",gap:8,alignItems:"center"}}>
                  <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--amber)"}}>🔮 KRONOS AI</span>
                  <span style={{fontFamily:"var(--mono)",fontSize:8,color:btcData.kronos.direction==="UP"?"var(--green)":btcData.kronos.direction==="DOWN"?"var(--red)":"var(--dim)"}}>
                    {btcData.kronos.direction} · {btcData.kronos.upProb?.toFixed(1)}% UP · {btcData.kronos.confidence}
                  </span>
                  <span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--dim)"}}>12B candles · AAAI 2026</span>
                </div>
              )}
              {btcData && btcData.markets?.filter(m=>m.edge>3).slice(0,3).map((m,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 6px",marginBottom:2,background:"rgba(245,158,11,.06)",borderRadius:2,border:"1px solid rgba(245,158,11,.15)"}}>
                  <span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--dim)",flex:1}}>{m.question.slice(0,50)}{m.question.length>50?"...":""}</span>
                  <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--green)",minWidth:35}}>+{m.edge}¢</span>
                  <span style={{fontFamily:"var(--mono)",fontSize:8,color:m.signal!=="SKIP"?"var(--amber)":"var(--dim)",minWidth:55}}>{m.signal}</span>
                  <span style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--dim)",minWidth:40}}>${(m.volume/1000).toFixed(0)}K vol</span>
                </div>
              ))}
              {!btcData && <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)",padding:"4px 0"}}>Click SCAN BTC to find short-term BTC prediction markets with edge</div>}
              {btcData && btcData.markets?.filter(m=>m.edge>3).length===0 && <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)",padding:"4px 0"}}>No edge found — momentum is NEUTRAL, check back when BTC moves ±0.5%</div>}
            </div>

            {/* NEGRISK PANEL — slides in below graph */}
            {arbTab && (
              <div style={{flex:"0 0 50%",borderTop:"1px solid rgba(168,85,247,.4)",display:"flex",flexDirection:"column",minHeight:0,background:"rgba(168,85,247,.03)"}}>

                {/* Arb header */}
                <div style={{padding:"6px 12px",borderBottom:"1px solid rgba(168,85,247,.2)",background:"rgba(168,85,247,.06)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:"var(--purple)",boxShadow:"0 0 6px var(--purple)"}}/>
                    <span style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--purple)",letterSpacing:2}}>NEGRISK ARB SCANNER</span>
                    <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)"}}>· {arbOpps.length} OPPORTUNITIES · ZERO RISK</span>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={scanArb} disabled={arbScanning} style={{fontFamily:"var(--mono)",fontSize:8,padding:"3px 8px",borderRadius:2,background:"rgba(168,85,247,.12)",border:"1px solid rgba(168,85,247,.4)",color:"var(--purple)",cursor:"pointer"}}>
                      {arbScanning?"⟳ SCANNING...":"⟳ RESCAN"}
                    </button>
                    <button onClick={()=>setArbTab(false)} style={{fontFamily:"var(--mono)",fontSize:8,padding:"3px 8px",borderRadius:2,background:"rgba(255,68,102,.1)",border:"1px solid rgba(255,68,102,.3)",color:"var(--red)",cursor:"pointer"}}>
                      ✕ CLOSE
                    </button>
                  </div>
                </div>

                {/* Arb cards — horizontal scroll */}
                <div style={{flex:1,overflow:"auto",padding:"8px 10px",display:"flex",gap:8,minHeight:0}}>
                  {arbScanning ? (
                    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:10,fontFamily:"var(--mono)",fontSize:10,color:"var(--purple)"}}>
                      <div className="spinner" style={{borderTopColor:"var(--purple)"}}/>
                      SCANNING 440+ MARKETS...
                    </div>
                  ) : arbOpps.map((opp,i)=>(
                    <div key={opp.id} style={{minWidth:240,border:`1px solid ${opp.confidence==="HIGH"?"rgba(0,255,136,.4)":opp.confidence==="MEDIUM"?"rgba(245,158,11,.35)":"rgba(74,112,144,.3)"}`,borderRadius:4,padding:"10px 12px",background:opp.confidence==="HIGH"?"rgba(0,255,136,.04)":opp.confidence==="MEDIUM"?"rgba(245,158,11,.04)":"var(--bg2)",display:"flex",flexDirection:"column",gap:7,flexShrink:0}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                        <div>
                          <div style={{fontFamily:"var(--sans)",fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:2}}>{opp.eventName}</div>
                          <span style={{fontFamily:"var(--mono)",fontSize:7,padding:"1px 4px",background:opp.confidence==="HIGH"?"rgba(0,255,136,.12)":"rgba(245,158,11,.12)",color:opp.confidence==="HIGH"?"var(--green)":"var(--amber)",borderRadius:2}}>{opp.confidence}</span>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontFamily:"var(--mono)",fontSize:20,fontWeight:700,color:opp.roiPct>20?"var(--green)":opp.roiPct>10?"var(--amber)":"var(--text)",lineHeight:1}}>+{opp.roiPct.toFixed(1)}%</div>
                          <div style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--dim)"}}>GUARANTEED</div>
                        </div>
                      </div>
                      <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--mid)",lineHeight:1.5}}>{opp.description}</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                        {[["YES SUM",opp.yesSum.toFixed(3),"var(--green)"],["MAX BET",`$${opp.maxPosition}`,"var(--amber)"],["LEGS",opp.outcomes,"var(--text)"],["PROFIT",`+$${(opp.maxPosition*opp.roiPct/100).toFixed(2)}`,"var(--green)"]].map(([l,v,col])=>(
                          <div key={l} style={{background:"var(--bg3)",borderRadius:3,padding:"4px 6px"}}>
                            <div style={{fontFamily:"var(--mono)",fontSize:7,color:"var(--dim)"}}>{l}</div>
                            <div style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:col}}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <button onClick={()=>executeArb(opp)} style={{fontFamily:"var(--mono)",fontSize:8,padding:"5px",borderRadius:3,background:"rgba(168,85,247,.15)",border:"1px solid rgba(168,85,247,.5)",color:"var(--purple)",cursor:"pointer",letterSpacing:1,fontWeight:700}}>
                        ⚡ EXECUTE {opp.outcomes} LEGS · +${(opp.maxPosition*opp.roiPct/100).toFixed(2)}
                      </button>
                    </div>
                  ))}
                </div>

              </div>
            )}

          </div>

          {/* RIGHT */}
          <div className="panel">
            <div className="ph">LIVE MISPRICING SIGNAL <span className="hi">{simResult?"ACTIVE":"IDLE"}</span></div>
            <div className="pb">
              {simResult && (
                <div className="mispricing-box">
                  <div className="mb-label">EDGE DETECTION</div>
                  <div className="mb-row"><span className="mb-l">MARKET</span><span className="mb-v">{topMarket?.question?.slice(0,22)}...</span></div>
                  <div className="mb-row"><span className="mb-l">MARKET ODDS</span><span className="mb-v">{((topMarket?.yesPrice||0.5)*100).toFixed(0)}¢ YES</span></div>
                  <div className="mb-row"><span className="mb-l">MIROFISH T-24H</span><span className="mb-v" style={{color:"var(--red)"}}>${wtiPrice.spot}</span></div>
                  <div className="mb-row"><span className="mb-l">IMPLIED ODDS</span><span className="mb-v" style={{color:"var(--amber)"}}>{((simResult.markets?.[0]?.true_prob||0.06)*100).toFixed(0)}¢</span></div>
                  <div className="mb-row"><span className="mb-l">EDGE</span><span className="mb-v" style={{color:"var(--cyan)"}}>+{((simResult.top_trade_edge||0.15)*100).toFixed(0)}¢</span></div>
                  <button className="short-btn">▼ {topDir} WTI — {((simResult.top_trade_edge||0.15)*100).toFixed(0)}¢ EDGE</button>
                </div>
              )}

              <div className="ph" style={{background:"transparent",border:"none",padding:"4px 0",marginBottom:4}}>MARKETS <span className="hi">{markets.length}</span></div>
              {markets.map(m=>(
                <div key={m.id} className="mkt-card">
                  <div className="mkt-q">{m.question}</div>
                  <div className="mkt-prices">
                    <span className="yes-p">YES {(m.yesPrice*100).toFixed(1)}¢</span>
                    <span className="no-p">NO {(m.noPrice*100).toFixed(1)}¢</span>
                  </div>
                  <div className="mkt-bar"><div className="mkt-fill" style={{width:`${m.yesPrice*100}%`}}/></div>
                  <div className="mkt-meta">
                    <span>VOL ${Math.round((m.volume||0)/1000)}K</span>
                    <span>LIQ ${Math.round((m.liquidity||0)/1000)}K</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* BOTTOM */}
          <div className="panel bottom-panel">

            {/* LEFT: PnL Curve + Edge Dist */}
            <div style={{width:190,borderRight:"1px solid var(--border)",display:"flex",flexDirection:"column",flexShrink:0,background:"var(--bg)"}}>
              <div className="ph">PnL CURVE <span className="hi">{trades.length} TRADES</span></div>
              <div style={{flex:1,padding:"6px 8px",overflow:"hidden",display:"flex",flexDirection:"column"}}>
                <Sparkline data={pnlHistory.length>1?pnlHistory:[0,0,0]} color={sessionPnl>=0?"#00ff88":"#ff4466"} height={95}/>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:3,fontSize:8,color:"var(--dim)"}}>
                  <span style={{color:sessionPnl>=0?"var(--green)":"var(--red)"}}>{sessionPnl>=0?"+$":"-$"}{Math.abs(sessionPnl).toFixed(2)}</span>
                  <span>W:{wins} L:{losses} · {winRate}%</span>
                </div>
                <div style={{marginTop:5,fontSize:7,color:"var(--dim)",letterSpacing:1,marginBottom:3}}>EDGE DIST +{edgeAvg}¢ AVG</div>
                <div style={{display:"flex",alignItems:"flex-end",gap:2,height:28}}>
                  {(edgeDist.length>0?edgeDist:Array(14).fill(15)).map((v,i)=>(
                    <div key={i} style={{flex:1,height:`${Math.max(10,(v/50)*100)}%`,background:v>25?"var(--green)":v>15?"var(--amber)":"var(--red)",borderRadius:"1px 1px 0 0",opacity:.8}}/>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT: Execution Log fills remaining space */}
            <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
              <div className="ph">
                EXECUTION LOG <span className="hi">{trades.length}</span>
                <span style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)",marginLeft:8}}>
                  BANKROLL: <span style={{color:"var(--amber)"}}>${bankroll.toFixed(2)}</span>
                </span>
              </div>
              <div style={{flex:1,overflow:"auto"}}>
                <div style={{display:"grid",gridTemplateColumns:"65px 50px 1fr 55px 55px 55px 70px 80px",gap:6,padding:"4px 8px",borderBottom:"1px solid var(--bright)",fontSize:8,color:"var(--dim)",letterSpacing:1}}>
                  <span>TIME</span><span>DIR</span><span>MARKET</span>
                  <span style={{textAlign:"right"}}>SIZE</span>
                  <span style={{textAlign:"right"}}>PRICE</span>
                  <span style={{textAlign:"right"}}>EDGE</span>
                  <span style={{textAlign:"right"}}>P&L</span>
                  <span style={{textAlign:"right"}}>ACTION</span>
                </div>
                {trades.length===0 && <div style={{fontSize:9,color:"var(--dim)",textAlign:"center",padding:"16px 0"}}>No trades yet — click AUTO TRADE</div>}
                {trades.map(t=>{
                  const pnl = t.pnlVal||0;
                  const isOpen = t.status==="open";
                  const sellTrade = () => {
                    const profit = pnl!==0 ? pnl : parseFloat(t.rawSize||0)*0.05;
                    const updated = trades.map(x => x.id===t.id ? {...x, status:"sold", pnlVal:profit} : x);
                    setTrades(updated); saveTrades(updated);
                    setBankroll(b => parseFloat((b + parseFloat(t.rawSize||0) + profit).toFixed(2)));
                    setSessionPnl(p => parseFloat((p + profit).toFixed(2)));
                    setPnlHistory(h => {const last=h.length>0?h[h.length-1]:0;return[...h,last+profit].slice(-60);});
                    setLiveFills(f => [{time:t.time,dir:"SELL",market:t.market,pnl:profit.toFixed(2),edge:t.edge,conf:"—"},...f].slice(0,30));
                  };
                  return (
                    <div key={t.id} style={{display:"grid",gridTemplateColumns:"65px 50px 1fr 55px 55px 55px 70px 80px",gap:6,alignItems:"center",padding:"4px 8px",borderBottom:"1px solid rgba(255,255,255,.02)",fontSize:9}}>
                      <span style={{color:"var(--dim)",fontSize:8}}>{t.time}{t.execution==="live"?<span style={{color:"#00ff88",marginLeft:3,fontSize:7}}>●LIVE</span>:<span style={{color:"#4a7090",marginLeft:3,fontSize:7}}>PAPER</span>}</span>
                      <span style={{color:t.dir==="NO"?"var(--red)":t.dir==="ARB"?"var(--purple)":"var(--green)",fontWeight:700}}>{t.dir}</span>
                      <span style={{color:"var(--mid)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.market}</span>
                      <span style={{textAlign:"right"}}>${t.rawSize}</span>
                      <span style={{textAlign:"right",color:"var(--amber)"}}>{t.rawPrice}</span>
                      <span style={{textAlign:"right",color:"var(--cyan)"}}>{t.edge}</span>
                      <span style={{textAlign:"right",fontWeight:700,color:pnl>0?"var(--green)":pnl<0?"var(--red)":"var(--dim)"}}>
                        {pnl!==0?(pnl>0?"+$":"-$")+Math.abs(pnl).toFixed(2):"--"}
                      </span>
                      <span style={{textAlign:"right"}}>
                        {isOpen ? (
                          <button onClick={sellTrade} style={{fontFamily:"var(--mono)",fontSize:8,padding:"2px 7px",borderRadius:2,background:"rgba(255,68,102,.15)",border:"1px solid rgba(255,68,102,.4)",color:"var(--red)",cursor:"pointer",letterSpacing:1}}>
                            SELL
                          </button>
                        ) : (
                          <span style={{fontSize:8,color:t.status==="sold"?"var(--amber)":pnl>0?"var(--green)":"var(--red)"}}>
                            {t.status==="sold"?"SOLD":pnl>0?"WIN":"LOSS"}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              {false && (
                <div style={{flex:1,overflow:"auto",padding:"6px 8px"}}>
                  {arbScanning && (
                    <div style={{fontFamily:"var(--mono)",fontSize:10,color:"var(--purple)",textAlign:"center",padding:"20px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                      <div className="spinner" style={{borderTopColor:"var(--purple)"}}/>
                      SCANNING {(440).toLocaleString()} MARKETS FOR NEGRISK...
                    </div>
                  )}
                  {!arbScanning && arbOpps.length===0 && (
                    <div style={{fontSize:9,color:"var(--dim)",textAlign:"center",padding:"16px 0"}}>
                      No arbitrage found — click ⚡ NEGRISK ARB to scan
                    </div>
                  )}
                  {!arbScanning && arbOpps.map((opp,i)=>(
                    <div key={opp.id} style={{border:`1px solid ${opp.confidence==="HIGH"?"rgba(0,255,136,.4)":opp.confidence==="MEDIUM"?"rgba(245,158,11,.4)":"rgba(74,112,144,.3)"}`,borderRadius:3,marginBottom:6,padding:"8px 10px",background:opp.confidence==="HIGH"?"rgba(0,255,136,.05)":opp.confidence==="MEDIUM"?"rgba(245,158,11,.05)":"var(--bg2)",display:"grid",gridTemplateColumns:"1fr auto",gap:"6px 12px"}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                          <span style={{fontFamily:"var(--mono)",fontSize:10,fontWeight:700,color:"var(--text)"}}>{opp.eventName}</span>
                          <span style={{fontFamily:"var(--mono)",fontSize:8,padding:"1px 5px",background:opp.confidence==="HIGH"?"rgba(0,255,136,.15)":"rgba(245,158,11,.15)",color:opp.confidence==="HIGH"?"var(--green)":"var(--amber)",borderRadius:2}}>{opp.confidence}</span>
                        </div>
                        <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--mid)",marginBottom:5,lineHeight:1.5}}>{opp.description}</div>
                        <div style={{display:"flex",gap:12,fontSize:8,fontFamily:"var(--mono)",color:"var(--dim)"}}>
                          <span>LEGS: <span style={{color:"var(--text)"}}>{opp.outcomes}</span></span>
                          <span>YES SUM: <span style={{color:"var(--green)"}}>{opp.yesSum.toFixed(3)}</span></span>
                          <span>MAX: <span style={{color:"var(--amber)"}}>${opp.maxPosition}</span></span>
                          <span>PROFIT: <span style={{color:"var(--green)"}}>+${(opp.maxPosition*opp.roiPct/100).toFixed(2)}</span></span>
                        </div>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"space-between"}}>
                        <div style={{fontFamily:"var(--mono)",fontSize:18,fontWeight:700,color:opp.roiPct>20?"var(--green)":opp.roiPct>10?"var(--amber)":"var(--text)"}}>+{opp.roiPct.toFixed(1)}%</div>
                        <div style={{fontFamily:"var(--mono)",fontSize:8,color:"var(--dim)",marginBottom:4}}>GUARANTEED ROI</div>
                        <button onClick={()=>scanArb()} style={{fontFamily:"var(--mono)",fontSize:8,padding:"4px 10px",borderRadius:2,background:"rgba(168,85,247,.15)",border:"1px solid rgba(168,85,247,.4)",color:"var(--purple)",cursor:"pointer",letterSpacing:1,whiteSpace:"nowrap"}}>
                          EXECUTE ARB →
                        </button>
                      </div>
                    </div>
                  ))}
                  <button onClick={scanArb} disabled={arbScanning} style={{width:"100%",marginTop:4,fontFamily:"var(--mono)",fontSize:9,padding:"6px",borderRadius:2,background:"rgba(168,85,247,.1)",border:"1px solid rgba(168,85,247,.3)",color:"var(--purple)",cursor:"pointer",letterSpacing:1}}>
                    {arbScanning?"⟳ SCANNING...":"⟳ RESCAN ALL MARKETS"}
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* STATUS BAR */}
        <div className="status-bar">
          <div className="sb-item"><span className="sb-label">MIROFISH</span><span className="sb-val cyan">{(60+Math.random()*35).toFixed(0)}% CONF</span></div>
          <div className="sb-item"><span className="sb-label">EDGE AVG</span><span className="sb-val amber">+{edgeAvg}¢</span></div>
          <div className="sb-item"><span className="sb-label">WTI</span><span className={`sb-val ${wtiPrice.change>=0?"green":"red"}`}>${wtiPrice.spot} {wtiPrice.change>=0?"▲":"▼"}{Math.abs(wtiPrice.change)}</span></div>
          <div className="sb-item"><span className="sb-label">CYCLE</span><span className="sb-val">#{cycleCount} · {autoRunning?"30s AUTO":"MANUAL"}</span></div>
          <div className="sb-item"><span className="sb-label">W/L</span><span className="sb-val">{wins}/{losses}</span></div>
          <div className="sb-item"><span className="sb-label">WIN RATE</span><span className="sb-val green">{winRate}%</span></div>
          <div className="sb-item"><span className="sb-label">SESSION PnL</span><span className={`sb-val ${sessionPnl>=0?"green":"red"}`}>{sessionPnl>=0?"+$":"-$"}{Math.abs(sessionPnl).toFixed(2)}</span></div>
          <div className="sb-item" style={{marginLeft:"auto"}}><span className="sb-label">SIGNALS/MIN</span><span className="sb-val">{autoRunning?"2.0":"0.0"}</span></div>
        </div>
      </div>
    </>
  );
}
